import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import SimplifiedHeader from '../components/layout/SimplifiedHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import TDSRegistrationModal from '../components/TDSRegistrationModal';
import TDSEducationalTimeline from '../components/tds/TDSEducationalTimeline';
import SIAOrb from '../components/shared/SIAOrb';
import BetaFoundersModal from '../components/BetaFoundersModal';
import { 
  Receipt, FileText, Calculator, Building2, Users, Calendar, 
  ChevronRight, CheckCircle2, AlertCircle, Download, Upload,
  Plus, Trash2, Edit, Eye, ArrowRight, Info, IndianRupee,
  FileSpreadsheet, Briefcase, User, CreditCard, Clock, Power,
  Menu, X, BookOpen, HelpCircle, FileQuestion, ChevronLeft,
  Play, Settings, Home, Hash, Building, Phone, Mail, MapPin,
  Mic, MicOff, Volume2
} from 'lucide-react';
import { trackActivity } from '../lib/activity';

// Form types
const FORM_TYPES = [
  {
    id: '24Q',
    title: 'Form 24Q',
    subtitle: 'TDS on Salaries',
    description: 'Quarterly statement for TDS on salary payments under Section 192',
    icon: Users,
    gradient: 'from-blue-500 to-cyan-500',
    sections: ['192 - Salary'],
    features: ['Annexure I - Employee Details', 'Annexure II - Salary Breakup (Q4)', 'Challan 281 Linking'],
    isLive: true
  },
  {
    id: '26Q',
    title: 'Form 26Q',
    subtitle: 'TDS on Non-Salary Payments',
    description: 'Quarterly statement for TDS on payments other than salary to residents',
    icon: FileText,
    gradient: 'from-purple-500 to-pink-500',
    sections: ['194A - Interest', '194C - Contractors', '194J - Professional Fees'],
    features: ['Section-wise breakup', 'Multiple deductees', 'Challan reconciliation'],
    isLive: false
  },
  {
    id: '27Q',
    title: 'Form 27Q',
    subtitle: 'TDS on Foreign Payments',
    description: 'Quarterly statement for TDS on payments to non-residents under Section 195',
    icon: Building2,
    gradient: 'from-emerald-500 to-teal-500',
    sections: ['195 - NRI Payments', '196D - Foreign Dividends', '196C - Foreign Bonds'],
    features: ['DTAA applicability', 'Country-wise reporting', 'Remittance details'],
    isLive: false
  }
];

// TDS Rate Chart Data for FY 2025-26 (AY 2026-27) - As amended by Finance Act, 2025
const TDS_RATE_CHART_DATA = [
  // Residents
  { section: '192', payment: 'Salary', rate: 'Normal Slab Rate', threshold: '-', category: 'resident' },
  { section: '192A', payment: 'Premature withdrawal from EPF', rate: '10%', threshold: 'Rs. 50,000', category: 'resident' },
  { section: '193', payment: 'Interest on Securities', rate: '10%', threshold: 'Rs. 10,000', category: 'resident' },
  { section: '194', payment: 'Dividend', rate: '10%', threshold: 'Rs. 5,000', category: 'resident' },
  { section: '194A', payment: 'Interest (other than securities)', rate: '10%', threshold: 'Rs. 50,000 (Senior: Rs. 1,00,000)', category: 'resident' },
  { section: '194B', payment: 'Winnings from Lottery/Crossword', rate: '30%', threshold: 'Rs. 10,000', category: 'resident' },
  { section: '194BA', payment: 'Winnings from Online Games', rate: '30%', threshold: 'Nil', category: 'resident' },
  { section: '194BB', payment: 'Winnings from Horse Race', rate: '30%', threshold: 'Rs. 10,000', category: 'resident' },
  { section: '194C', payment: 'Contractor - Individual/HUF', rate: '1%', threshold: 'Rs. 30,000 single / Rs. 1,00,000 aggregate', category: 'resident' },
  { section: '194C', payment: 'Contractor - Others', rate: '2%', threshold: 'Rs. 30,000 single / Rs. 1,00,000 aggregate', category: 'resident' },
  { section: '194D', payment: 'Insurance Commission', rate: '5%', threshold: 'Rs. 15,000', category: 'resident' },
  { section: '194DA', payment: 'Life Insurance Policy Payout', rate: '5%', threshold: 'Rs. 1,00,000', category: 'resident' },
  { section: '194E', payment: 'Non-Resident Sportsman/Entertainer', rate: '20%', threshold: '-', category: 'resident' },
  { section: '194EE', payment: 'NSS Withdrawal', rate: '10%', threshold: 'Rs. 2,500', category: 'resident' },
  { section: '194F', payment: 'Repurchase of UTI/MF Units', rate: '20%', threshold: '-', category: 'resident' },
  { section: '194G', payment: 'Commission on Lottery Tickets', rate: '5%', threshold: 'Rs. 15,000', category: 'resident' },
  { section: '194H', payment: 'Commission/Brokerage', rate: '5%', threshold: 'Rs. 15,000', category: 'resident' },
  { section: '194-I(a)', payment: 'Rent - Plant & Machinery', rate: '2%', threshold: 'Rs. 2,40,000', category: 'resident' },
  { section: '194-I(b)', payment: 'Rent - Land/Building/Furniture', rate: '10%', threshold: 'Rs. 2,40,000', category: 'resident' },
  { section: '194-IA', payment: 'Transfer of Immovable Property', rate: '1%', threshold: 'Rs. 50,00,000', category: 'resident' },
  { section: '194-IB', payment: 'Rent by Individual/HUF (Non-audit)', rate: '5%', threshold: 'Rs. 50,000/month', category: 'resident' },
  { section: '194-IC', payment: 'JDA - Monetary Consideration', rate: '10%', threshold: '-', category: 'resident' },
  { section: '194J(a)', payment: 'Professional/Technical Fees (Call Centre)', rate: '2%', threshold: 'Rs. 30,000', category: 'resident' },
  { section: '194J(b)', payment: 'Professional/Technical Fees (Others)', rate: '10%', threshold: 'Rs. 30,000', category: 'resident' },
  { section: '194K', payment: 'Income from Units (MF/UTI)', rate: '10%', threshold: 'Rs. 5,000', category: 'resident' },
  { section: '194LA', payment: 'Compensation on Land Acquisition', rate: '10%', threshold: 'Rs. 2,50,000', category: 'resident' },
  { section: '194LBA(1)', payment: 'Business Trust Distribution', rate: '10%', threshold: '-', category: 'resident' },
  { section: '194LBB', payment: 'Income from Investment Fund', rate: '10%', threshold: '-', category: 'resident' },
  { section: '194LBC', payment: 'Income from Securitization Trust', rate: '25% (Individual) / 30% (Others)', threshold: '-', category: 'resident' },
  { section: '194M', payment: 'Payment to Contractor by Individual/HUF', rate: '5%', threshold: 'Rs. 50,00,000', category: 'resident' },
  { section: '194N', payment: 'Cash Withdrawal (ITR filer)', rate: '2%', threshold: 'Above Rs. 1 Crore', category: 'resident' },
  { section: '194N', payment: 'Cash Withdrawal (Non-ITR filer)', rate: '2% / 5%', threshold: 'Rs. 20L-1Cr / Above Rs. 1Cr', category: 'resident' },
  { section: '194-O', payment: 'E-commerce Operator Payment', rate: '1%', threshold: 'Rs. 5,00,000', category: 'resident' },
  { section: '194P', payment: 'Senior Citizen Pension (75+)', rate: 'Slab Rate', threshold: '-', category: 'resident' },
  { section: '194Q', payment: 'Purchase of Goods', rate: '0.1%', threshold: 'Rs. 50,00,000', category: 'resident' },
  { section: '194R', payment: 'Benefits/Perquisites from Business', rate: '10%', threshold: 'Rs. 20,000', category: 'resident' },
  { section: '194S', payment: 'Transfer of Virtual Digital Asset (VDA)', rate: '1%', threshold: 'Rs. 50,000 (Specified) / Rs. 10,000 (Others)', category: 'resident' },
  { section: '194T', payment: 'Payment to Partner of Firm', rate: '10%', threshold: 'Rs. 20,000', category: 'resident' },
  // Non-Residents (Section 195)
  { section: '195', payment: 'Interest on Foreign Currency Loan (Sec 194LC)', rate: '5%', threshold: '-', category: 'non-resident' },
  { section: '195', payment: 'Interest on Infrastructure Debt Fund (Sec 194LB)', rate: '5%', threshold: '-', category: 'non-resident' },
  { section: '195', payment: 'Interest on Rupee Denominated Bond (Sec 194LD)', rate: '5%', threshold: '-', category: 'non-resident' },
  { section: '195', payment: 'Dividend', rate: '20%', threshold: '-', category: 'non-resident' },
  { section: '195', payment: 'Royalty (Agreement after 31/3/1976)', rate: '10%', threshold: '-', category: 'non-resident' },
  { section: '195', payment: 'Technical Services (Fees)', rate: '10%', threshold: '-', category: 'non-resident' },
  { section: '195', payment: 'Long Term Capital Gain (Sec 112)', rate: '10%', threshold: '-', category: 'non-resident' },
  { section: '195', payment: 'Short Term Capital Gain (Sec 111A)', rate: '15%', threshold: '-', category: 'non-resident' },
  { section: '195', payment: 'Any Other Income', rate: '30%', threshold: '-', category: 'non-resident' },
  { section: '196B', payment: 'Income from GDR (Foreign Currency)', rate: '10%', threshold: '-', category: 'non-resident' },
  { section: '196C', payment: 'Income from Foreign Currency Bonds', rate: '10%', threshold: '-', category: 'non-resident' },
  { section: '196D', payment: 'Income of FIIs from Securities', rate: '20%', threshold: '-', category: 'non-resident' },
];

// Resources for sidebar
const RESOURCES = [
  { id: 1, title: 'TDS Rate Chart FY 2025-26', icon: FileText, type: 'PDF', hasModal: true },
  { id: 2, title: 'Form 24Q Instructions', icon: BookOpen, type: 'Guide' },
  { id: 3, title: 'Section 192 Explained', icon: HelpCircle, type: 'FAQ' },
  { id: 4, title: 'Challan 281 Format', icon: FileQuestion, type: 'Template' },
  { id: 5, title: 'TDS Due Dates', icon: Calendar, type: 'Info' },
];

// Assessment Years
const ASSESSMENT_YEARS = ['2026-27', '2025-26', '2024-25'];
const QUARTERS = [
  { id: 'Q1', label: 'Q1 (Apr-Jun)', months: 'April to June', dueDate: '31st July' },
  { id: 'Q2', label: 'Q2 (Jul-Sep)', months: 'July to September', dueDate: '31st October' },
  { id: 'Q3', label: 'Q3 (Oct-Dec)', months: 'October to December', dueDate: '31st January' },
  { id: 'Q4', label: 'Q4 (Jan-Mar)', months: 'January to March', dueDate: '31st May' }
];

// TDS Sections for Form 24Q
const TDS_SECTIONS = [
  { code: '192', name: 'Salary', rate: 'As per slab' },
  { code: '192A', name: 'Premature withdrawal from EPF', rate: '10%' }
];

// Deduction reasons
const DEDUCTION_REASONS = [
  { code: 'A', desc: 'Lower/No deduction - Certificate u/s 197' },
  { code: 'B', desc: 'Lower deduction due to Form 15G/15H' },
  { code: 'C', desc: 'Higher deduction u/s 206AA (No PAN)' },
  { code: 'Z', desc: 'No deduction - Income below threshold' }
];

export default function TDSReturnPage() {
  const navigate = useNavigate();
  // Stages: 'select' | 'start' | 'timeline' | 'form' | 'challan' | 'annexure1' | 'annexure2' | 'summary'
  const [stage, setStage] = useState('select');
  const [selectedForm, setSelectedForm] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Hamburger menu - default closed

  // Track simulator start (logged-in only, fire-and-forget)
  useEffect(() => {
    trackActivity({
      simulator_id: 'tds_return',
      simulator_name: 'TDS Return Filing Simulator',
      action: 'started',
    });
  }, []);
  
  // Registration Modal
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [pendingFormSelection, setPendingFormSelection] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  
  // Form Page SIA Introduction
  const [formIntroPlayed, setFormIntroPlayed] = useState(false);
  const [formSiaMessage, setFormSiaMessage] = useState('');
  const [formSiaSpeaking, setFormSiaSpeaking] = useState(false);
  const [skippedTimeline, setSkippedTimeline] = useState(false); // Track if user skipped timeline
  const [helpPromptShown, setHelpPromptShown] = useState(false); // Track if help prompt shown
  const formAudioRef = React.useRef(null);
  
  // Demo Challan Data
  const demoChallanData = {
    company: 'Demo Company Private Limited',
    tan: 'DELP12345A',
    challans: [
      { month: 'April', bsr: '0012345', date: '07-May-2025', amount: '45,000', cin: 'BSR0012345070525001' },
      { month: 'May', bsr: '0012346', date: '07-Jun-2025', amount: '52,000', cin: 'BSR0012346070625001' },
      { month: 'June', bsr: '0012347', date: '07-Jul-2025', amount: '48,500', cin: 'BSR0012347070725001' },
    ]
  };
  
  // Timeline Animation States
  const [timelinePhase, setTimelinePhase] = useState(0);
  const [activePod, setActivePod] = useState(-1);
  const [siaMessage, setSiaMessage] = useState('');
  const [siaFootnote, setSiaFootnote] = useState(''); // Educational footnote at bottom
  const [isSiaSpeaking, setIsSiaSpeaking] = useState(false); // Controls orb glow
  const [showDeadlineAlert, setShowDeadlineAlert] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [ttsEnabled, setTtsEnabled] = useState(true); // SIA TTS voice narration
  
  // TTS Audio Ref for SIA voice playback
  const siaAudioRef = React.useRef(null);
  
  // Web Audio API Sound Effects System - Professional & Pleasant Sounds
  const audioContextRef = React.useRef(null);
  
  const getAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  };
  
  // SIA TTS - Speak with warm voice
  const siaSpeakTTS = async (text) => {
    if (!ttsEnabled) return;
    
    try {
      // Stop any currently playing audio
      if (siaAudioRef.current) {
        siaAudioRef.current.pause();
        siaAudioRef.current = null;
      }
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/tds/sia-speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: 'nova' }) // nova = warm, energetic
      });
      
      const data = await response.json();
      if (data.success && data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        siaAudioRef.current = audio;
        
        audio.onended = () => {
          siaAudioRef.current = null;
        };
        
        audio.onerror = () => {
          siaAudioRef.current = null;
        };
        
        audio.play().catch((err) => {
          console.log('TTS playback failed:', err);
          siaAudioRef.current = null;
        });
      }
    } catch (err) {
      console.log('SIA TTS error:', err);
    }
  };
  
  // Stop SIA audio
  const stopSiaAudio = () => {
    if (siaAudioRef.current) {
      siaAudioRef.current.pause();
      siaAudioRef.current = null;
    }
  };
  
  // SIA Form Introduction Dialogues - Different based on skip status
  const getFormIntroDialogues = () => {
    if (skippedTimeline) {
      // User skipped the timeline - give full introduction
      return [
        "Hi! I am SIA, your dedicated assistant. I will help you in filing and understanding Form 24Q.",
        "It seems you have skipped the Process Flow. No worries! Let me give you a quick overview.",
        "Form 24Q is the quarterly TDS return for salaries. The due date to file is 31st July for Quarter 1.",
        "In the previous section, we deposited challans for April, May, and June. You can find them in the Resources section on your left.",
        "Now, what you are required to do is to go through this Form and check all the data. Feel free to make mistakes - that's how we learn!",
        "If you need any help, I am right here. Just click on me anytime!",
      ];
    } else {
      // User completed the timeline - shorter intro
      return [
        "Great job completing the Process Flow! Now let's file your TDS return.",
        "In the previous section, we deposited challans for April, May, and June. You can find them in the Resources section on your left.",
        "Now, go through this Form and check all the data. Feel free to make mistakes - that's how we learn!",
        "If you need any help, I am right here. Just click on me anytime!",
      ];
    }
  };
  
  // Track which dialogue is playing for arrow animation
  const [showResourcesArrow, setShowResourcesArrow] = useState(false);
  
  // Speak a single message
  const speakSiaMessage = async (text) => {
    const ttsText = text.replace(/\bSIA\b/g, 'Siya');
    setFormSiaMessage(text);
    setFormSiaSpeaking(true);
    
    try {
      if (formAudioRef.current) {
        formAudioRef.current.pause();
        formAudioRef.current = null;
      }
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/tds/sia-speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: ttsText, voice: 'nova' })
      });
      
      const data = await response.json();
      
      if (data.success && data.audio_base64) {
        await new Promise((resolve) => {
          const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          formAudioRef.current = audio;
          
          audio.onended = () => {
            formAudioRef.current = null;
            resolve();
          };
          
          audio.onerror = () => {
            formAudioRef.current = null;
            resolve();
          };
          
          audio.play().catch(() => resolve());
        });
      }
    } catch (err) {
      console.log('TTS error:', err);
    }
    
    setFormSiaSpeaking(false);
  };
  
  const playFormIntro = async () => {
    if (formIntroPlayed) return;
    setFormIntroPlayed(true);
    
    const dialogues = getFormIntroDialogues();
    
    // Find the index of "Resources" dialogue to open sidebar
    const resourcesDialogueIndex = dialogues.findIndex(d => d.includes('Resources section'));
    
    for (let i = 0; i < dialogues.length; i++) {
      const text = dialogues[i];
      
      // Open sidebar when mentioning Resources
      if (i === resourcesDialogueIndex) {
        setShowResourcesArrow(true);
        setSidebarOpen(true);
      }
      
      // Close sidebar after Resources dialogue (on next dialogue)
      if (i === resourcesDialogueIndex + 1) {
        setShowResourcesArrow(false);
        setSidebarOpen(false);
      }
      
      await speakSiaMessage(text);
      
      // Small pause between dialogues
      await new Promise(r => setTimeout(r, 800));
    }
    
    // Clear message after last dialogue
    setTimeout(() => {
      setFormSiaMessage('');
    }, 3000);
    
    // Show periodic help prompt after 45 seconds
    setTimeout(() => {
      if (!helpPromptShown) {
        setHelpPromptShown(true);
        speakSiaMessage("Do you need help somewhere? Feel free to ask me anything about Form 24Q!");
        setTimeout(() => setFormSiaMessage(''), 8000);
      }
    }, 45000);
  };
  
  // Trigger form intro when stage changes to 'form'
  useEffect(() => {
    if (stage === 'form' && !formIntroPlayed) {
      // Delay slightly for page to render
      const timer = setTimeout(() => {
        playFormIntro();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [stage, formIntroPlayed]);
  
  
  // Soft chime - pleasant notification
  const playSoftChime = (baseFreq = 523) => {
    if (!soundEnabled) return;
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(baseFreq, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.5, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.6);
    } catch (e) { console.log('Audio error:', e); }
  };
  
  // Gentle whoosh for transitions
  const playWhoosh = () => {
    if (!soundEnabled) return;
    try {
      const ctx = getAudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(800, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.4);
      osc.frequency.setValueAtTime(400, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.4);
      gain.gain.setValueAtTime(0.06, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } catch (e) { console.log('Audio error:', e); }
  };
  
  // Success sound - warm and pleasant
  const playSuccessSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = getAudioContext();
      const notes = [392, 523, 659]; // G4, C5, E5 - major chord
      notes.forEach((freq, i) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, ctx.currentTime);
          gain.gain.setValueAtTime(0.06, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.5);
        }, i * 120);
      });
    } catch (e) { console.log('Audio error:', e); }
  };
  
  // Alert sound - gentle but attention-grabbing
  const playAlertTone = () => {
    if (!soundEnabled) return;
    try {
      const ctx = getAudioContext();
      [0, 300].forEach((delay) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(880, ctx.currentTime);
          gain.gain.setValueAtTime(0.08, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.15);
        }, delay);
      });
    } catch (e) { console.log('Audio error:', e); }
  };
  
  // Completion fanfare - warm and satisfying
  const playCompletionSound = () => {
    if (!soundEnabled) return;
    try {
      const ctx = getAudioContext();
      const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
      notes.forEach((freq, i) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, ctx.currentTime);
          gain.gain.setValueAtTime(0.07, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.6);
        }, i * 180);
      });
    } catch (e) { console.log('Audio error:', e); }
  };
  
  // Check if user is already registered
  useEffect(() => {
    const tdsSession = localStorage.getItem('practiq_tds_registration_session');
    if (tdsSession) {
      try {
        const session = JSON.parse(tdsSession);
        if (session.emailVerified) {
          setIsRegistered(true);
        }
      } catch (e) {
        console.log('Invalid TDS session');
      }
    }
  }, []);
  
  // Form 24Q Main Details (Form Header)
  const [formData, setFormData] = useState({
    assessmentYear: '2026-27',
    financialYear: '2025-26',
    quarter: 'Q1',
    formType: 'Regular', // Regular or Correction
    previousRRN: '', // For correction returns
    // Deductor Details - Pre-filled for demo
    tanNumber: 'DELP12345A',
    panNumber: 'AAACD1234A',
    deductorName: 'Demo Company Private Limited',
    deductorCategory: 'Company', // Company, Government, Individual etc
    branchDivision: 'Head Office',
    // Address
    flatNo: '501',
    buildingName: 'Tech Park Tower',
    roadStreet: 'MG Road',
    area: 'Cyber City',
    city: 'Gurugram',
    state: 'Haryana',
    pincode: '122001',
    // Contact
    telephone: '0124-4567890',
    email: 'accounts@democompany.in',
    // Responsible Person
    responsiblePersonName: 'Rajesh Kumar',
    responsiblePersonDesignation: 'CFO',
    responsiblePersonAddress: '501, Tech Park Tower',
    responsiblePersonCity: 'Gurugram',
    responsiblePersonState: 'Haryana',
    responsiblePersonPincode: '122001',
    responsiblePersonPhone: '9876543210',
    responsiblePersonEmail: 'rajesh.kumar@democompany.in'
  });

  // Challan Details (Challan 281)
  const [challans, setChallans] = useState([
    {
      id: 1,
      bsrCode: '0510462',
      challanSerial: '12345',
      depositDate: '2024-11-07',
      depositAmount: 155000,
      tdsAmount: 150000,
      surcharge: 0,
      educationCess: 5000,
      interest: 0,
      fee234E: 0,
      others: 0,
      minorHead: '200', // 200=TDS payable by taxpayer
      bankName: 'State Bank of India',
      chequeNo: '',
      bookEntry: false
    }
  ]);

  // Annexure I - Deductee (Employee) Details
  const [deductees, setDeductees] = useState([
    {
      id: 1,
      slNo: 1,
      employeeRefNo: 'EMP001',
      pan: 'ABCPK1234A',
      name: 'Rajesh Kumar',
      dateOfPayment: '2024-12-31',
      section: '192',
      grossSalary: 500000,
      tdsDeducted: 50000,
      surcharge: 0,
      educationCess: 2000,
      totalTax: 52000,
      deductionReason: '',
      certificateNo: '',
      challanId: 1
    },
    {
      id: 2,
      slNo: 2,
      employeeRefNo: 'EMP002',
      pan: 'DEFPS5678B',
      name: 'Priya Sharma',
      dateOfPayment: '2024-12-31',
      section: '192',
      grossSalary: 600000,
      tdsDeducted: 60000,
      surcharge: 0,
      educationCess: 2400,
      totalTax: 62400,
      deductionReason: '',
      certificateNo: '',
      challanId: 1
    },
    {
      id: 3,
      slNo: 3,
      employeeRefNo: 'EMP003',
      pan: 'GHIAV9012C',
      name: 'Amit Verma',
      dateOfPayment: '2024-12-31',
      section: '192',
      grossSalary: 450000,
      tdsDeducted: 40600,
      surcharge: 0,
      educationCess: 0,
      totalTax: 40600,
      deductionReason: '',
      certificateNo: '',
      challanId: 1
    }
  ]);

  // Annexure II - Salary Breakup (Q4 only)
  const [annexure2, setAnnexure2] = useState([]);

  // Modals
  const [showAddChallan, setShowAddChallan] = useState(false);
  const [showAddDeductee, setShowAddDeductee] = useState(false);
  const [showTDSRateChart, setShowTDSRateChart] = useState(false);
  const [rateChartFilter, setRateChartFilter] = useState('all'); // 'all', 'resident', 'non-resident'
  
  // TDS Submission Success States
  const [showTDSSuccessModal, setShowTDSSuccessModal] = useState(false);
  const [tdsTokenNumber, setTdsTokenNumber] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  // System Activation Animation States
  const [isActivating, setIsActivating] = useState(false);
  const [activationStep, setActivationStep] = useState(0); // 0-4 for step lighting
  const [siaActivated, setSiaActivated] = useState(false);
  const [newChallan, setNewChallan] = useState({
    bsrCode: '', challanSerial: '', depositDate: '', depositAmount: '',
    tdsAmount: '', surcharge: '0', educationCess: '', interest: '0',
    fee234E: '0', minorHead: '200', bankName: ''
  });
  const [newDeductee, setNewDeductee] = useState({
    employeeRefNo: '', pan: '', name: '', dateOfPayment: '',
    section: '192', grossSalary: '', tdsDeducted: '', educationCess: '',
    deductionReason: '', certificateNo: '', challanId: ''
  });

  // Calculations
  const totalTDSDeducted = deductees.reduce((sum, d) => sum + (d.tdsDeducted || 0), 0);
  const totalCessDeducted = deductees.reduce((sum, d) => sum + (d.educationCess || 0), 0);
  const totalTaxDeducted = deductees.reduce((sum, d) => sum + (d.totalTax || 0), 0);
  const totalChallanAmount = challans.reduce((sum, c) => sum + (c.depositAmount || 0), 0);
  const totalGrossSalary = deductees.reduce((sum, d) => sum + (d.grossSalary || 0), 0);

  const handleSelectForm = (form) => {
    if (!form.isLive) return;
    
    // Check if user is already registered
    if (isRegistered) {
      setSelectedForm(form);
      setStage('start');
    } else {
      // Show registration modal
      setPendingFormSelection(form);
      setShowRegistrationModal(true);
    }
  };
  
  const handleRegistrationSuccess = (userData) => {
    setIsRegistered(true);
    if (pendingFormSelection) {
      setSelectedForm(pendingFormSelection);
      setStage('start');
      setPendingFormSelection(null);
    }
  };

  const handleStartFiling = () => {
    // Trigger System Activation Animation
    setIsActivating(true);
    setSiaActivated(true);
    
    // Sequential step lighting (steps 1-4 light up over 1.5s)
    setTimeout(() => setActivationStep(1), 200);
    setTimeout(() => setActivationStep(2), 500);
    setTimeout(() => setActivationStep(3), 800);
    setTimeout(() => setActivationStep(4), 1100);
    
    // Reset SIA pulse after 600ms
    setTimeout(() => setSiaActivated(false), 600);
    
    // Transition to timeline after activation animation completes (2s)
    setTimeout(() => {
      setIsActivating(false);
      setActivationStep(0);
      setStage('timeline');
      // Note: TDSEducationalTimeline component handles the animation internally
      // startTimelineAnimation() is not needed when using the timeline component
    }, 2000);
  };
  
  // Timeline Animation Controller - Slower, Educational Flow
  const startTimelineAnimation = () => {
    // Reset all states
    setTimelinePhase(0);
    setActivePod(-1);
    setSiaMessage('');
    setSiaFootnote('');
    setIsSiaSpeaking(false);
    setShowDeadlineAlert(false);
    setIsMerging(false);
    stopSiaAudio(); // Stop any existing TTS audio
    
    // Helper to show SIA speaking with TTS
    const siaSay = (header, footnote, duration = 4000) => {
      setSiaMessage(header);
      setSiaFootnote(footnote);
      setIsSiaSpeaking(true);
      setTimeout(() => setIsSiaSpeaking(false), duration);
      // Trigger TTS for the footnote (the educational text SIA speaks)
      siaSpeakTTS(footnote);
    };
    
    // ========== PHASE 0: Introduction (0-4s) ==========
    setTimeout(() => {
      siaSay(
        'Welcome to TDS Process Flow!',
        "Hey there! Before we dive in, let me explain something important. TDS Deposit and TDS Return are two different things — don't mix them up!",
        4000
      );
      playSoftChime(523);
    }, 500);
    
    // ========== PHASE 1: Explain TDS Deposit (4-9s) ==========
    setTimeout(() => {
      setTimelinePhase(1);
      siaSay(
        'Understanding TDS Deposit',
        "TDS Deposit means paying the tax you've deducted from salaries to the government. You do this every month by the 7th of the next month. Simple, right?",
        5000
      );
      playWhoosh();
    }, 4500);
    
    // ========== PHASE 2: Explain TDS Return (9-14s) ==========
    setTimeout(() => {
      siaSay(
        'Understanding TDS Return',
        "TDS Return is where you file Form 24Q — linking all employee PANs, showing their salary breakups, and reporting all the TDS you deposited. It's like your quarterly report card to the government!",
        5000
      );
    }, 9500);
    
    // ========== PHASE 3: Pro Tip with humor (14-19s) ==========
    setTimeout(() => {
      siaSay(
        'Pro Tip from SIA 😊',
        "Oh, and just because the due date says 31st July doesn't mean you wait until then! File early to avoid last-minute portal crashes and glitches. Trust me, you'll thank yourself later!",
        5000
      );
      playSoftChime(659);
    }, 14500);
    
    // ========== PHASE 4: Let's Begin (19-21s) ==========
    setTimeout(() => {
      setTimelinePhase(2);
      siaSay(
        'Let\'s See the Q1 Timeline',
        "Now let me walk you through Quarter 1. Watch how each month's TDS deposit flows into the final return filing...",
        3000
      );
      playWhoosh();
    }, 19500);
    
    // ========== PHASE 5: April Pod (22-27s) ==========
    setTimeout(() => {
      setTimelinePhase(3);
      setActivePod(0);
      siaSay(
        'April — First Month of Q1',
        "April's TDS must be deposited by 7th May. This covers all salaries paid in April. The deposit links to Challan 281.",
        5000
      );
      playSuccessSound();
    }, 22500);
    
    // ========== PHASE 6: May Pod (27-32s) ==========
    setTimeout(() => {
      setTimelinePhase(4);
      setActivePod(1);
      siaSay(
        'May — Second Month of Q1',
        "May's TDS follows the same pattern — deposit by 7th June. Keep your challans organized, you'll need them for the return!",
        5000
      );
      playSuccessSound();
    }, 27500);
    
    // ========== PHASE 7: June Pod (32-37s) ==========
    setTimeout(() => {
      setTimelinePhase(5);
      setActivePod(2);
      siaSay(
        'June — Final Month of Q1',
        "June's TDS deposit is due by 7th July. Once this is done, you have all three months covered. Now it's time to compile everything!",
        5000
      );
      playSuccessSound();
    }, 32500);
    
    // ========== PHASE 8: Show other quarters preview (37-40s) ==========
    setTimeout(() => {
      setTimelinePhase(6);
      siaSay(
        'Other Quarters',
        "Q2, Q3, and Q4 follow the same monthly deposit pattern. Each quarter has its own return due date.",
        3000
      );
    }, 37500);
    
    // ========== PHASE 9: Deadline Alert (40-44s) ==========
    setTimeout(() => {
      setTimelinePhase(7);
      setShowDeadlineAlert(true);
      siaSay(
        '⚠️ Important Deadline!',
        "31st July is the Q1 Return filing deadline. All your April, May, and June deposits come together in Form 24Q. Don't wait till the last day!",
        4000
      );
      playAlertTone();
    }, 40500);
    
    // ========== PHASE 10: Merge Animation (44-48s) ==========
    setTimeout(() => {
      setTimelinePhase(8);
      setShowDeadlineAlert(false);
      setIsMerging(true);
      siaSay(
        'Data Synthesis in Progress...',
        "Watch as all three months merge into your Form 24Q. This is where employee details, salary breakups, and TDS amounts come together!",
        4000
      );
      playWhoosh();
    }, 44500);
    
    // ========== PHASE 11: Completion (48-52s) ==========
    setTimeout(() => {
      setTimelinePhase(9);
      siaSay(
        '✓ Form 24Q Ready!',
        "Perfect! Your Q1 Form 24Q is now initialized and ready for filing. Let's get started with the actual form!",
        4000
      );
      playCompletionSound();
    }, 48500);
    
    // ========== PHASE 12: Transition to form (52s) ==========
    setTimeout(() => {
      setStage('form');
      // Reset timeline states
      setTimelinePhase(0);
      setActivePod(-1);
      setSiaMessage('');
      setSiaFootnote('');
      setIsSiaSpeaking(false);
      setShowDeadlineAlert(false);
      setIsMerging(false);
    }, 52500);
  };

  const handleAddChallan = () => {
    if (newChallan.bsrCode && newChallan.challanSerial && newChallan.depositAmount) {
      const challan = {
        id: challans.length + 1,
        ...newChallan,
        depositAmount: parseInt(newChallan.depositAmount) || 0,
        tdsAmount: parseInt(newChallan.tdsAmount) || 0,
        surcharge: parseInt(newChallan.surcharge) || 0,
        educationCess: parseInt(newChallan.educationCess) || 0,
        interest: parseInt(newChallan.interest) || 0,
        fee234E: parseInt(newChallan.fee234E) || 0
      };
      setChallans([...challans, challan]);
      setNewChallan({
        bsrCode: '', challanSerial: '', depositDate: '', depositAmount: '',
        tdsAmount: '', surcharge: '0', educationCess: '', interest: '0',
        fee234E: '0', minorHead: '200', bankName: ''
      });
      setShowAddChallan(false);
    }
  };

  const handleAddDeductee = () => {
    if (newDeductee.pan && newDeductee.name && newDeductee.grossSalary) {
      const deductee = {
        id: deductees.length + 1,
        slNo: deductees.length + 1,
        ...newDeductee,
        grossSalary: parseInt(newDeductee.grossSalary) || 0,
        tdsDeducted: parseInt(newDeductee.tdsDeducted) || 0,
        educationCess: parseInt(newDeductee.educationCess) || 0,
        totalTax: (parseInt(newDeductee.tdsDeducted) || 0) + (parseInt(newDeductee.educationCess) || 0),
        challanId: parseInt(newDeductee.challanId) || 1
      };
      setDeductees([...deductees, deductee]);
      setNewDeductee({
        employeeRefNo: '', pan: '', name: '', dateOfPayment: '',
        section: '192', grossSalary: '', tdsDeducted: '', educationCess: '',
        deductionReason: '', certificateNo: '', challanId: ''
      });
      setShowAddDeductee(false);
    }
  };

  const handleDeleteChallan = (id) => setChallans(challans.filter(c => c.id !== id));
  const handleDeleteDeductee = (id) => setDeductees(deductees.filter(d => d.id !== id));

  const goBack = () => {
    if (stage === 'start') setStage('select');
    else if (stage === 'form') setStage('start');
    else if (stage === 'challan') setStage('form');
    else if (stage === 'annexure1') setStage('challan');
    else if (stage === 'annexure2') setStage('annexure1');
    else if (stage === 'summary') setStage(formData.quarter === 'Q4' ? 'annexure2' : 'annexure1');
  };

  const getStepNumber = (currentStage) => {
    const steps = ['form', 'challan', 'annexure1'];
    if (formData.quarter === 'Q4') steps.push('annexure2');
    steps.push('summary');
    return steps.indexOf(currentStage) + 1;
  };

  const getTotalSteps = () => formData.quarter === 'Q4' ? 5 : 4;

  // Progress steps for sidebar
  const getProgressSteps = () => {
    const steps = [
      { label: 'Deductor Details', stage: 'form' },
      { label: 'Challan Details', stage: 'challan' },
      { label: 'Annexure I - Employees', stage: 'annexure1' }
    ];
    if (formData.quarter === 'Q4') {
      steps.push({ label: 'Annexure II - Salary Breakup', stage: 'annexure2' });
    }
    steps.push({ label: 'Summary & Submit', stage: 'summary' });
    return steps;
  };

  // ==================== SIDEBAR COMPONENT ====================
  const Sidebar = ({ currentStage }) => {
    const steps = getProgressSteps();
    const currentIndex = steps.findIndex(s => s.stage === currentStage);

    return (
      <div className={`${sidebarOpen ? 'w-72' : 'w-16'} bg-[#0d1220] border-r border-white/10 transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-400" />
              <span className="text-white font-medium">Form 24Q</span>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
            {sidebarOpen ? <X className="w-5 h-5 text-gray-400" /> : <Menu className="w-5 h-5 text-gray-400" />}
          </button>
        </div>

        {sidebarOpen && (
          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
            {/* Filing Progress */}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Filing Progress</p>
              <div className="space-y-2">
                {steps.map((step, i) => {
                  const isDone = i < currentIndex;
                  const isActive = i === currentIndex;
                  return (
                    <div key={i} className={`flex items-center gap-3 p-2 rounded-lg ${isActive ? 'bg-blue-500/10 border border-blue-500/20' : ''}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${isDone ? 'bg-green-500 text-white' : isActive ? 'bg-blue-500 text-white' : 'bg-white/10 text-gray-400'}`}>
                        {isDone ? '✓' : i + 1}
                      </div>
                      <span className={`text-sm ${isActive ? 'text-white' : isDone ? 'text-green-400' : 'text-gray-500'}`}>{step.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Return Info */}
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-xs text-gray-500 mb-2">Return Details</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">FY:</span><span className="text-white">{formData.financialYear}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Quarter:</span><span className="text-white">{formData.quarter}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Due Date:</span><span className="text-amber-400">{QUARTERS.find(q => q.id === formData.quarter)?.dueDate}</span></div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-xs text-gray-500 mb-2">TDS Summary</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Employees:</span><span className="text-white">{deductees.length}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Total TDS:</span><span className="text-green-400">₹{totalTaxDeducted.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Challans:</span><span className="text-white">₹{totalChallanAmount.toLocaleString()}</span></div>
              </div>
            </div>

            {/* Resources */}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Resources</p>
              {RESOURCES.slice(0, 3).map((resource) => (
                <button 
                  key={resource.id} 
                  onClick={() => resource.hasModal && setShowTDSRateChart(true)}
                  className="w-full p-2 mb-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-blue-500/30 transition-all text-left"
                >
                  <div className="flex items-center gap-2">
                    <resource.icon className="w-4 h-4 text-blue-400" />
                    <span className="text-xs text-white truncate">{resource.title}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!sidebarOpen && (
          <div className="flex-1 p-2 space-y-2">
            {getProgressSteps().map((step, i) => {
              const currentIndex = getProgressSteps().findIndex(s => s.stage === currentStage);
              const isDone = i < currentIndex;
              const isActive = i === currentIndex;
              return (
                <div key={i} className={`w-full p-3 rounded-lg flex justify-center ${isActive ? 'bg-blue-500/20' : ''}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${isDone ? 'bg-green-500 text-white' : isActive ? 'bg-blue-500 text-white' : 'bg-white/10 text-gray-400'}`}>
                    {isDone ? '✓' : i + 1}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ==================== ROBOTIC TIMELINE ====================
  const renderTimeline = () => {
    const months = [
      { id: 'apr', label: 'APR', dueDate: '7th MAY', quarter: 'Q1', isActive: true },
      { id: 'may', label: 'MAY', dueDate: '7th JUNE', quarter: 'Q1', isActive: true },
      { id: 'jun', label: 'JUN', dueDate: '7th JULY', quarter: 'Q1', isActive: true },
      { id: 'jul', label: 'JUL', dueDate: '7th AUG', quarter: 'Q2', isActive: false },
      { id: 'aug', label: 'AUG', dueDate: '7th SEP', quarter: 'Q2', isActive: false },
      { id: 'sep', label: 'SEP', dueDate: '7th OCT', quarter: 'Q2', isActive: false },
      { id: 'oct', label: 'OCT', dueDate: '7th NOV', quarter: 'Q3', isActive: false },
      { id: 'nov', label: 'NOV', dueDate: '7th DEC', quarter: 'Q3', isActive: false },
      { id: 'dec', label: 'DEC', dueDate: '7th JAN', quarter: 'Q3', isActive: false },
      { id: 'jan', label: 'JAN', dueDate: '7th FEB', quarter: 'Q4', isActive: false },
      { id: 'feb', label: 'FEB', dueDate: '7th MAR', quarter: 'Q4', isActive: false },
      { id: 'mar', label: 'MAR', dueDate: '15th MAR', quarter: 'Q4', isActive: false },
    ];

    return (
      <div className="min-h-screen bg-[#050510] flex flex-col items-center justify-center relative overflow-hidden">
        {/* Background Grid Effect */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
        
        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-purple-500/10 rounded-full blur-[100px]" />
        
        {/* Header - Fixed at top with higher z-index to prevent overlap */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-center z-50">
          <h1 className="text-2xl font-bold text-white mb-1">
            TDS <span className="text-purple-400">Process Flow</span>
          </h1>
          <p className="text-purple-300/60 text-sm">Quarter 1 - Financial Year 2025-26</p>
        </div>
        
        {/* SIA Header Message Badge - Below header */}
        <div className={`absolute top-[85px] left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${siaMessage ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <div className="flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-900/60 to-blue-900/60 border border-purple-500/40 backdrop-blur-sm shadow-lg">
            <div className={`w-3 h-3 rounded-full transition-all duration-300 ${isSiaSpeaking ? 'bg-cyan-400 animate-pulse scale-125' : 'bg-purple-400'}`} />
            <span className="text-white text-sm font-medium">{siaMessage}</span>
          </div>
        </div>
        
        {/* SIA Glowing Orb - Bottom Right */}
        <div className="absolute bottom-32 right-8 z-30">
          <div className={`relative w-16 h-16 transition-all duration-500 ${isSiaSpeaking ? 'scale-110' : 'scale-100'}`}>
            {/* Speaking Pulse Rings */}
            {isSiaSpeaking && (
              <>
                <div className="absolute -inset-4 rounded-full border-2 border-cyan-400/50 animate-ping" style={{ animationDuration: '1.5s' }} />
                <div className="absolute -inset-2 rounded-full border border-purple-400/30 animate-ping" style={{ animationDuration: '2s' }} />
              </>
            )}
            
            {/* Orb Glow */}
            <div className={`absolute inset-0 rounded-full blur-xl transition-all duration-300 ${isSiaSpeaking ? 'bg-cyan-400/40' : 'bg-purple-500/20'}`} />
            
            {/* Orb Core */}
            <div className={`relative w-full h-full rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
              isSiaSpeaking 
                ? 'bg-gradient-to-br from-cyan-900/80 to-purple-900/80 border-cyan-400/60 shadow-[0_0_30px_rgba(34,211,238,0.4)]' 
                : 'bg-gradient-to-br from-purple-900/50 to-blue-900/50 border-purple-500/40'
            }`}>
              <span className="text-white font-bold text-sm">SIA</span>
            </div>
          </div>
          <p className={`text-center text-xs mt-2 transition-colors duration-300 ${isSiaSpeaking ? 'text-cyan-400' : 'text-gray-500'}`}>
            {isSiaSpeaking ? 'Speaking...' : 'Setu AI'}
          </p>
        </div>
        
        {/* SIA Footnote - Educational Text at Bottom */}
        <div className={`absolute bottom-20 left-1/2 -translate-x-1/2 w-full max-w-3xl px-8 z-20 transition-all duration-700 ${siaFootnote ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="bg-gradient-to-r from-purple-900/40 via-blue-900/40 to-purple-900/40 border border-purple-500/20 rounded-2xl px-6 py-4 backdrop-blur-sm">
            <div className="flex items-start gap-4">
              <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-all duration-300 ${isSiaSpeaking ? 'bg-cyan-500/30 border-2 border-cyan-400' : 'bg-purple-500/30 border border-purple-400/50'}`}>
                <span className="text-xs font-bold text-white">SIA</span>
              </div>
              <p className="text-gray-200 text-sm leading-relaxed">{siaFootnote}</p>
            </div>
          </div>
        </div>

        {/* Main Timeline Container - Positioned below header */}
        <div className="relative w-full max-w-5xl px-8 mt-24">
          
          {/* Holographic Rail - Only show after phase 2 */}
          <div className={`relative h-2 bg-gradient-to-r from-transparent via-purple-500/30 to-transparent rounded-full transition-all duration-1000 ${timelinePhase >= 3 ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}>
            {/* Rail Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400/50 to-transparent blur-sm" />
            
            {/* Scanner Light */}
            <div 
              className={`absolute top-1/2 -translate-y-1/2 w-24 h-8 bg-gradient-to-r from-transparent via-cyan-400 to-transparent blur-md transition-all duration-[2000ms] ${timelinePhase >= 3 ? 'opacity-100' : 'opacity-0'}`}
              style={{ 
                left: `${Math.min((activePod + 1) * 33, 100)}%`,
                transform: 'translateX(-50%) translateY(-50%)'
              }}
            />
          </div>
          
          {/* Q1 Active Pods - APR, MAY, JUN - Only show from phase 3 */}
          <div className={`flex justify-center gap-8 mt-8 transition-all duration-700 ${timelinePhase >= 3 ? 'opacity-100' : 'opacity-0'}`}>
            {months.slice(0, 3).map((month, index) => (
              <div 
                key={month.id}
                className={`relative transition-all duration-700 ${
                  isMerging 
                    ? 'translate-x-0 scale-0 opacity-0' 
                    : activePod >= index 
                      ? 'scale-100 opacity-100' 
                      : 'scale-75 opacity-30'
                }`}
                style={{ 
                  transitionDelay: isMerging ? `${index * 100}ms` : `${index * 200}ms`,
                  transform: isMerging ? `translateX(${(1 - index) * 100}px) scale(0)` : undefined
                }}
              >
                {/* Pod Glow Ring */}
                <div className={`absolute -inset-4 rounded-2xl transition-all duration-500 ${
                  activePod === index 
                    ? 'bg-cyan-500/20 animate-pulse' 
                    : activePod > index 
                      ? 'bg-green-500/10' 
                      : ''
                }`} />
                
                {/* Glass Pod */}
                <div className={`relative w-28 h-36 rounded-xl backdrop-blur-md border-2 transition-all duration-500 ${
                  activePod > index 
                    ? 'bg-green-900/30 border-green-400/50 shadow-[0_0_30px_rgba(34,197,94,0.3)]' 
                    : activePod === index 
                      ? 'bg-cyan-900/30 border-cyan-400/50 shadow-[0_0_30px_rgba(34,211,238,0.3)]' 
                      : 'bg-purple-900/20 border-purple-500/30'
                }`}>
                  
                  {/* Status Icon */}
                  <div className={`absolute -top-3 -right-3 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
                    activePod > index ? 'bg-green-500 scale-100' : 'bg-gray-600 scale-75'
                  }`}>
                    {activePod > index ? (
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    ) : (
                      <div className="w-2 h-2 bg-gray-400 rounded-full" />
                    )}
                  </div>
                  
                  {/* Month Label */}
                  <div className="flex flex-col items-center justify-center h-full p-3">
                    <span className={`text-2xl font-bold transition-colors duration-300 ${
                      activePod >= index ? 'text-white' : 'text-purple-300/50'
                    }`}>
                      {month.label}
                    </span>
                    
                    {/* Due Date */}
                    <div className={`mt-3 px-2 py-1 rounded-md text-xs font-mono transition-all duration-500 ${
                      activePod === index 
                        ? 'bg-red-500/30 text-red-300 animate-pulse' 
                        : activePod > index 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-purple-500/20 text-purple-300/50'
                    }`}>
                      {month.dueDate}
                    </div>
                    
                    {/* Deposit Label */}
                    <span className="text-[10px] text-gray-400 mt-2">TDS Deposit</span>
                  </div>
                  
                  {/* Scanner Beam Effect */}
                  {activePod === index && (
                    <div className="absolute inset-0 overflow-hidden rounded-xl">
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-scan-down" />
                    </div>
                  )}
                </div>
                
                {/* Connection Line to Rail */}
                <div className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full w-0.5 h-8 transition-all duration-500 ${
                  activePod >= index ? 'bg-gradient-to-b from-purple-500/50 to-cyan-400' : 'bg-purple-500/20'
                }`} />
              </div>
            ))}
          </div>
          
          {/* Other Quarters (Inactive/Coming Soon) - Show from phase 6 */}
          <div className={`flex justify-center gap-4 mt-12 transition-all duration-700 ${timelinePhase >= 6 ? 'opacity-100' : 'opacity-0'}`}>
            {['Q2', 'Q3', 'Q4'].map((quarter, qIndex) => (
              <div key={quarter} className="flex flex-col items-center">
                <div className="flex gap-2 mb-2">
                  {months.slice(3 + qIndex * 3, 6 + qIndex * 3).map((month) => (
                    <div 
                      key={month.id}
                      className="w-12 h-14 rounded-lg bg-gray-800/50 border border-gray-600/30 flex items-center justify-center"
                    >
                      <span className="text-xs text-gray-500 font-medium">{month.label}</span>
                    </div>
                  ))}
                </div>
                <span className="text-xs text-gray-500 font-mono">{quarter} - Coming</span>
              </div>
            ))}
          </div>
          
          {/* Deadline Alert */}
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-500 z-30 ${
            showDeadlineAlert && !isMerging ? 'opacity-100 scale-100' : 'opacity-0 scale-50 pointer-events-none'
          }`}>
            <div className="relative">
              {/* Alert Pulse Rings */}
              <div className="absolute inset-0 rounded-2xl bg-red-500/20 animate-ping" />
              <div className="absolute -inset-4 rounded-3xl border-2 border-red-500/30 animate-pulse" />
              
              <div className="relative px-8 py-6 rounded-2xl bg-gradient-to-br from-red-900/80 to-orange-900/80 border-2 border-red-500/50 backdrop-blur-md shadow-[0_0_60px_rgba(239,68,68,0.4)]">
                <div className="flex items-center gap-4">
                  <AlertCircle className="w-10 h-10 text-red-400 animate-pulse" />
                  <div>
                    <p className="text-red-300 text-sm font-medium mb-1">Q1 RETURN DEADLINE</p>
                    <p className="text-3xl font-bold text-white">31st JULY</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Master Core - Form 24Q (Merge Target) */}
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-1000 z-40 ${
            isMerging ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
          }`}>
            <div className="relative">
              {/* Core Energy Rings */}
              <div className={`absolute -inset-8 rounded-full border-4 border-cyan-400/30 transition-all duration-700 ${timelinePhase >= 9 ? 'scale-150 opacity-0' : 'scale-100 opacity-100'} animate-spin-slow`} />
              <div className={`absolute -inset-4 rounded-full border-2 border-purple-400/50 transition-all duration-500 ${timelinePhase >= 9 ? 'scale-125 opacity-0' : 'scale-100 opacity-100'} animate-reverse-spin`} />
              
              {/* Particle Effects */}
              {[...Array(8)].map((_, i) => (
                <div 
                  key={i}
                  className="absolute w-2 h-2 bg-cyan-400 rounded-full animate-orbit"
                  style={{
                    animationDelay: `${i * 0.2}s`,
                    transformOrigin: '100px center'
                  }}
                />
              ))}
              
              {/* Core */}
              <div className={`relative w-40 h-40 rounded-full bg-gradient-to-br from-[#1a0a3a] via-[#0a1a3a] to-[#0a0a2a] border-4 transition-all duration-700 ${
                timelinePhase >= 9 
                  ? 'border-green-400 shadow-[0_0_80px_rgba(34,197,94,0.5)]' 
                  : 'border-purple-400 shadow-[0_0_60px_rgba(139,92,246,0.5)]'
              } flex items-center justify-center`}>
                
                {/* Inner Glow */}
                <div className={`absolute inset-4 rounded-full transition-all duration-500 ${
                  timelinePhase >= 9 ? 'bg-green-500/30' : 'bg-purple-500/30'
                } blur-md`} />
                
                {/* Core Content */}
                <div className="relative text-center z-10">
                  <FileText className={`w-10 h-10 mx-auto mb-2 transition-colors duration-500 ${
                    timelinePhase >= 9 ? 'text-green-400' : 'text-cyan-400'
                  }`} />
                  <span className="text-white font-bold text-lg">Form 24Q</span>
                  <p className="text-cyan-300/60 text-xs mt-1">Q1 FY 25-26</p>
                </div>
                
                {/* Success Checkmark */}
                <div className={`absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-green-500 flex items-center justify-center transition-all duration-500 ${
                  timelinePhase >= 9 ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                }`}>
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Voice Toggle, Sound Toggle & Skip Button */}
        <div className="absolute bottom-8 right-8 flex items-center gap-3">
          {/* SIA Voice Toggle */}
          <button 
            onClick={() => {
              setTtsEnabled(!ttsEnabled);
              if (ttsEnabled) stopSiaAudio();
            }}
            className={`px-3 py-2 rounded-lg border transition-all text-sm flex items-center gap-2 ${
              ttsEnabled 
                ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/30' 
                : 'bg-white/5 border-white/10 text-gray-500 hover:text-white hover:bg-white/10'
            }`}
            data-testid="voice-toggle"
          >
            {ttsEnabled ? (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
                SIA Voice ON
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
                SIA Voice OFF
              </>
            )}
          </button>
          {/* Sound Effects Toggle */}
          <button 
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`px-3 py-2 rounded-lg border transition-all text-sm flex items-center gap-2 ${
              soundEnabled 
                ? 'bg-purple-500/20 border-purple-500/30 text-purple-300 hover:bg-purple-500/30' 
                : 'bg-white/5 border-white/10 text-gray-500 hover:text-white hover:bg-white/10'
            }`}
            data-testid="sound-toggle"
          >
            {soundEnabled ? (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
                Sound ON
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
                Sound OFF
              </>
            )}
          </button>
          <button 
            onClick={() => {
              stopSiaAudio();
              setStage('form');
            }}
            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 transition-all text-sm"
          >
            Skip Animation →
          </button>
        </div>
        
        {/* CSS Animations */}
        <style>{`
          @keyframes scan-down {
            0% { transform: translateY(-100%); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(3600%); opacity: 0; }
          }
          .animate-scan-down {
            animation: scan-down 1.5s ease-in-out infinite;
          }
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin-slow {
            animation: spin-slow 8s linear infinite;
          }
          @keyframes reverse-spin {
            from { transform: rotate(360deg); }
            to { transform: rotate(0deg); }
          }
          .animate-reverse-spin {
            animation: reverse-spin 6s linear infinite;
          }
          @keyframes orbit {
            0% { transform: rotate(0deg) translateX(60px) rotate(0deg); opacity: 0; }
            20% { opacity: 1; }
            80% { opacity: 1; }
            100% { transform: rotate(360deg) translateX(60px) rotate(-360deg); opacity: 0; }
          }
          .animate-orbit {
            animation: orbit 3s linear infinite;
          }
        `}</style>
      </div>
    );
  };

  // ==================== FORM SELECTION ====================
  const renderFormSelection = () => (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-4">
          <Receipt className="w-4 h-4 text-purple-400" />
          <span className="text-sm text-purple-400 font-medium">TDS Return Filing Simulator</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Select TDS Return Form</h1>
        <p className="text-gray-400 max-w-2xl mx-auto">Practice filing TDS returns in a 100% risk-free environment.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {FORM_TYPES.map((form) => (
          <Card 
            key={form.id}
            className={`group bg-gradient-to-br from-[#111827] to-[#0D1220] border border-white/10 transition-all duration-500 overflow-hidden flex flex-col ${form.isLive ? 'hover:border-purple-500/50 cursor-pointer' : 'opacity-70 cursor-not-allowed'}`}
            onClick={() => handleSelectForm(form)}
            data-testid={`form-card-${form.id}`}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${form.gradient} opacity-0 ${form.isLive ? 'group-hover:opacity-5' : ''} transition-opacity duration-500`}></div>
            <CardContent className="p-6 relative z-10 flex flex-col flex-1">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${form.gradient} flex items-center justify-center shadow-lg ${form.isLive ? 'group-hover:scale-110' : ''} transition-transform duration-300`}>
                  <form.icon className="w-7 h-7 text-white" />
                </div>
                {form.isLive ? (
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>Live
                  </Badge>
                ) : (
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">Coming Soon</Badge>
                )}
              </div>
              <h3 className="text-xl font-semibold text-white mb-1">{form.title}</h3>
              <p className={`text-sm font-medium mb-3 ${form.isLive ? 'text-purple-400' : 'text-gray-500'}`}>{form.subtitle}</p>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed min-h-[48px]">{form.description}</p>
              <div className="space-y-2 mb-4 flex-1">
                {form.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className={`w-4 h-4 ${form.isLive ? 'text-purple-400' : 'text-gray-600'}`} />
                    <span className={form.isLive ? 'text-gray-300' : 'text-gray-500'}>{feature}</span>
                  </div>
                ))}
              </div>
              <Button 
                className={`w-full mt-auto ${form.isLive ? `bg-gradient-to-r ${form.gradient} text-white border-0` : 'bg-gray-800/50 text-gray-500 cursor-not-allowed'}`}
                disabled={!form.isLive}
                onClick={(e) => { e.stopPropagation(); handleSelectForm(form); }}
                data-testid={`start-filing-${form.id}`}
              >
                {form.isLive ? 'Start Filing' : 'Coming Soon'}
                {form.isLive && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Due Dates */}
      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/20">
        <div className="flex items-start gap-4">
          <Info className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-white font-medium mb-2">TDS Return Due Dates (FY 2025-26)</h4>
            <div className="grid md:grid-cols-4 gap-4 text-sm">
              {QUARTERS.map(q => (
                <div key={q.id}><span className="text-gray-400">{q.label}:</span><span className="text-white ml-2">{q.dueDate}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ==================== START SCREEN ====================
  const renderStartScreen = () => (
    <div className="min-h-[80vh] flex items-center justify-center relative overflow-hidden">
      {/* System Activation Circuit Lines Animation */}
      {isActivating && (
        <>
          {/* Circuit lines radiating from center */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" style={{ overflow: 'visible' }}>
            <defs>
              <linearGradient id="circuitGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#06b6d4" stopOpacity="0" />
                <stop offset="50%" stopColor="#06b6d4" stopOpacity="1" />
                <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="circuitGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#a855f7" stopOpacity="0" />
                <stop offset="50%" stopColor="#a855f7" stopOpacity="1" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* Line to left (sidebar direction) */}
            <line x1="50%" y1="45%" x2="5%" y2="30%" stroke="url(#circuitGradient1)" strokeWidth="3" filter="url(#glow)"
              className="animate-circuit-line-left" style={{ strokeDasharray: '1000', strokeDashoffset: '1000' }} />
            <line x1="50%" y1="50%" x2="0%" y2="50%" stroke="url(#circuitGradient2)" strokeWidth="2" filter="url(#glow)"
              className="animate-circuit-line-left" style={{ strokeDasharray: '1000', strokeDashoffset: '1000', animationDelay: '0.1s' }} />
            <line x1="50%" y1="55%" x2="5%" y2="70%" stroke="url(#circuitGradient1)" strokeWidth="3" filter="url(#glow)"
              className="animate-circuit-line-left" style={{ strokeDasharray: '1000', strokeDashoffset: '1000', animationDelay: '0.2s' }} />
            
            {/* Line to top (header direction) */}
            <line x1="50%" y1="40%" x2="30%" y2="0%" stroke="url(#circuitGradient2)" strokeWidth="2" filter="url(#glow)"
              className="animate-circuit-line-up" style={{ strokeDasharray: '1000', strokeDashoffset: '1000' }} />
            <line x1="50%" y1="40%" x2="50%" y2="0%" stroke="url(#circuitGradient1)" strokeWidth="3" filter="url(#glow)"
              className="animate-circuit-line-up" style={{ strokeDasharray: '1000', strokeDashoffset: '1000', animationDelay: '0.15s' }} />
            <line x1="50%" y1="40%" x2="70%" y2="0%" stroke="url(#circuitGradient2)" strokeWidth="2" filter="url(#glow)"
              className="animate-circuit-line-up" style={{ strokeDasharray: '1000', strokeDashoffset: '1000', animationDelay: '0.25s' }} />
            
            {/* Line to right (SIA orb direction) */}
            <line x1="50%" y1="50%" x2="95%" y2="85%" stroke="url(#circuitGradient1)" strokeWidth="3" filter="url(#glow)"
              className="animate-circuit-line-right" style={{ strokeDasharray: '1000', strokeDashoffset: '1000', animationDelay: '0.1s' }} />
            
            {/* Extra diagonal circuit lines */}
            <line x1="50%" y1="45%" x2="15%" y2="20%" stroke="url(#circuitGradient2)" strokeWidth="2" filter="url(#glow)"
              className="animate-circuit-line-left" style={{ strokeDasharray: '1000', strokeDashoffset: '1000', animationDelay: '0.3s' }} />
            <line x1="50%" y1="55%" x2="15%" y2="80%" stroke="url(#circuitGradient1)" strokeWidth="2" filter="url(#glow)"
              className="animate-circuit-line-left" style={{ strokeDasharray: '1000', strokeDashoffset: '1000', animationDelay: '0.35s' }} />
          </svg>
          
          {/* Central burst effect */}
          <div className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
            <div className="w-48 h-48 rounded-full bg-cyan-400/30 animate-ping" style={{ animationDuration: '0.8s' }} />
            <div className="absolute inset-4 rounded-full bg-purple-500/20 animate-ping" style={{ animationDuration: '0.6s', animationDelay: '0.1s' }} />
          </div>
          
          {/* Particle effects */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-particle"
              style={{
                left: '50%',
                top: '45%',
                background: i % 2 === 0 ? '#06b6d4' : '#a855f7',
                boxShadow: `0 0 10px ${i % 2 === 0 ? '#06b6d4' : '#a855f7'}`,
                animationDelay: `${i * 0.05}s`,
                '--particle-angle': `${i * 30}deg`,
              }}
            />
          ))}
        </>
      )}
      
      <div className="flex-1 bg-gradient-to-b from-[#0a0a1a] via-[#0d1025] to-[#0a0a1a] flex flex-col items-center justify-center p-6 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-radial from-blue-500/20 via-transparent to-transparent blur-3xl pointer-events-none" />

        <div className="mb-6">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 transition-all duration-300 ${isActivating ? 'border-cyan-400 bg-cyan-500/20 shadow-lg shadow-cyan-500/30' : ''}`}>
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-400 font-medium">{selectedForm?.title || 'Form 24Q'}</span>
          </div>
        </div>

        <h1 className={`text-3xl md:text-4xl font-bold text-white mb-2 transition-all duration-500 ${isActivating ? 'scale-105' : ''}`}>
          TDS <span className={`transition-colors duration-300 ${isActivating ? 'text-cyan-400' : 'text-blue-400'}`}>Filing Bot</span>
        </h1>
        <p className={`text-white/50 text-sm mb-8 transition-all duration-300 ${isActivating ? 'text-cyan-300/70' : ''}`}>
          {isActivating ? 'System Initializing...' : 'Powered by PractiQ Simulator'}
        </p>

        <button 
          onClick={handleStartFiling} 
          disabled={isActivating}
          data-testid="power-button" 
          className={`group relative w-32 h-32 md:w-40 md:h-40 mb-12 focus:outline-none transition-all duration-300 ${isActivating ? 'scale-110' : ''}`}
        >
          {/* Activation burst rings */}
          {isActivating && (
            <>
              <div className="absolute inset-[-20px] rounded-full border-2 border-cyan-400 animate-ping" style={{ animationDuration: '0.5s' }} />
              <div className="absolute inset-[-40px] rounded-full border border-purple-500 animate-ping" style={{ animationDuration: '0.7s', animationDelay: '0.1s' }} />
              <div className="absolute inset-[-60px] rounded-full border border-cyan-300/50 animate-ping" style={{ animationDuration: '0.9s', animationDelay: '0.2s' }} />
            </>
          )}
          
          <div className={`absolute inset-0 rounded-full transition-all duration-500 ${isActivating ? 'bg-cyan-500/40 blur-3xl' : 'bg-blue-500/20 blur-2xl group-hover:bg-blue-400/30'}`} />
          <div className={`absolute inset-2 rounded-full border-4 transition-all duration-300 ${isActivating ? 'border-cyan-400 shadow-lg shadow-cyan-500/50' : 'border-blue-500/50 group-hover:border-blue-400'}`} />
          <div className={`absolute inset-4 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 ${isActivating ? 'bg-gradient-to-br from-[#0a2a3a] to-[#1a0a3a]' : 'bg-gradient-to-br from-[#1a1a2e] to-[#0d0d1a] group-hover:from-[#1e1e3a] group-hover:to-[#12122a]'}`}>
            <Power className={`w-12 h-12 md:w-16 md:h-16 transition-all duration-300 ${isActivating ? 'text-cyan-300 animate-pulse' : 'text-blue-400 group-hover:text-blue-300'}`} />
          </div>
          <div className="absolute inset-0 rounded-full border-2 border-blue-400/0 group-hover:border-blue-400/50 group-hover:animate-ping transition-all duration-300 pointer-events-none" />
        </button>
        <p className={`text-lg mb-12 transition-all duration-300 ${isActivating ? 'text-cyan-400 font-medium' : 'text-white/60'}`}>
          {isActivating ? 'SYSTEM ACTIVATED' : 'Press To Start'}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-4xl">
          {[
            { num: '01', text: 'Enter deductor details - TAN, PAN, Address' },
            { num: '02', text: 'Add Challan 281 payment details' },
            { num: '03', text: 'Fill Annexure I - Employee TDS records' },
            { num: '04', text: 'Review summary and submit return' }
          ].map((step, i) => (
            <div 
              key={i} 
              className={`flex items-start gap-3 p-4 rounded-xl border transition-all duration-500 ${
                isActivating && activationStep > i 
                  ? 'bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border-cyan-400/50 shadow-lg shadow-cyan-500/20 scale-105' 
                  : 'bg-white/5 border-white/10'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <span className={`text-xl font-bold transition-colors duration-300 ${
                isActivating && activationStep > i ? 'text-cyan-400' : 'text-blue-400/50'
              }`}>{step.num}</span>
              <p className={`text-xs leading-relaxed transition-colors duration-300 ${
                isActivating && activationStep > i ? 'text-white' : 'text-white/70'
              }`}>{step.text}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* CSS for custom animations */}
      <style>{`
        @keyframes circuit-line-left {
          0% { stroke-dashoffset: 1000; opacity: 0; }
          20% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes circuit-line-up {
          0% { stroke-dashoffset: 1000; opacity: 0; }
          20% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes circuit-line-right {
          0% { stroke-dashoffset: 1000; opacity: 0; }
          20% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        .animate-circuit-line-left {
          animation: circuit-line-left 1.2s ease-out forwards;
        }
        .animate-circuit-line-up {
          animation: circuit-line-up 1s ease-out forwards;
        }
        .animate-circuit-line-right {
          animation: circuit-line-right 1.4s ease-out forwards;
        }
        @keyframes particle {
          0% { 
            transform: translate(-50%, -50%) rotate(var(--particle-angle)) translateX(0) scale(1);
            opacity: 1;
          }
          100% { 
            transform: translate(-50%, -50%) rotate(var(--particle-angle)) translateX(300px) scale(0);
            opacity: 0;
          }
        }
        .animate-particle {
          animation: particle 1.5s ease-out forwards;
        }
      `}</style>
    </div>
  );

  // ==================== DEDUCTOR FORM ====================
  const renderDeductorForm = () => (
    <div className="min-h-[80vh] flex relative">
      {/* Hamburger Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={`fixed top-24 left-4 z-50 p-3 rounded-xl border transition-all ${
          sidebarOpen 
            ? 'bg-purple-500/20 border-purple-500/30 text-purple-300' 
            : 'bg-[#111827] border-white/10 text-white hover:bg-white/10'
        }`}
        data-testid="sidebar-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
      
      {/* Arrow Animation pointing to hamburger menu */}
      {showResourcesArrow && !sidebarOpen && (
        <div className="fixed top-24 left-20 z-50 flex items-center gap-2 animate-bounce">
          <div className="flex items-center">
            <ArrowRight className="w-6 h-6 text-cyan-400 rotate-180" />
            <ArrowRight className="w-6 h-6 text-cyan-400 rotate-180 -ml-3 opacity-60" />
          </div>
          <span className="text-cyan-300 text-sm font-medium bg-cyan-500/20 px-3 py-1 rounded-full">Click here!</span>
        </div>
      )}
      
      {/* Collapsible Sidebar */}
      <div className={`fixed top-20 left-0 h-[calc(100vh-5rem)] bg-[#0a0f1c] border-r border-white/10 transition-all duration-300 z-40 ${
        sidebarOpen ? 'w-72 translate-x-0' : 'w-0 -translate-x-full'
      } overflow-hidden`}>
        <div className="w-72 h-full overflow-y-auto p-4 pt-16">
          {/* Form 24Q Progress */}
          <div className="mb-6">
            <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-purple-400" />
              Form 24Q Progress
            </h3>
            <div className="space-y-2">
              {[
                { id: 'form', label: 'Deductor Details', icon: Building },
                { id: 'challan', label: 'Challan Details', icon: Receipt },
                { id: 'annexure1', label: 'Annexure I', icon: Users },
                { id: 'annexure2', label: 'Annexure II', icon: Calculator },
                { id: 'summary', label: 'Summary & Submit', icon: CheckCircle2 },
              ].map((item, idx) => (
                <div 
                  key={item.id}
                  className={`flex items-center gap-3 p-2 rounded-lg ${
                    stage === item.id ? 'bg-purple-500/20 text-purple-300' : 'text-gray-400'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Resources Section with Challans */}
          <div className="mb-6">
            <h3 className={`text-white font-semibold flex items-center gap-2 mb-3 ${showResourcesArrow ? 'animate-pulse' : ''}`}>
              <BookOpen className="w-5 h-5 text-cyan-400" />
              Resources
              {showResourcesArrow && (
                <span className="ml-2 px-2 py-0.5 bg-cyan-500/20 text-cyan-300 text-xs rounded-full animate-pulse">New!</span>
              )}
            </h3>
            
            {/* Challans Deposited */}
            <div className={`p-3 rounded-lg border mb-3 ${showResourcesArrow ? 'border-cyan-500/50 bg-cyan-500/10' : 'border-white/10 bg-[#111827]'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-green-400" />
                  Challans Deposited
                </span>
                <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded">3</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Q1 FY 2025-26 | TDS on Salaries</p>
              
              {/* Mini Challan List */}
              <div className="space-y-2">
                {demoChallanData.challans.map((challan, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-[#0a0f1c] rounded border border-white/5">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-3 h-3 text-green-400" />
                      <span className="text-xs text-gray-300">{challan.month}</span>
                    </div>
                    <span className="text-xs text-green-300 font-medium">₹{challan.amount}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-2 pt-2 border-t border-white/10 flex justify-between">
                <span className="text-xs text-gray-400">Total</span>
                <span className="text-sm text-green-400 font-bold">₹1,45,500</span>
              </div>
            </div>
            
            {/* TDS Rate Chart */}
            <button 
              onClick={() => setShowTDSRateChart(true)}
              className="w-full flex items-center gap-2 p-2 bg-[#111827] rounded-lg border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 transition-colors text-sm"
            >
              <FileText className="w-4 h-4" />
              TDS Rate Chart FY 2025-26
            </button>
          </div>
          
          {/* Due Date */}
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" />
              <div>
                <p className="text-xs text-amber-300 font-medium">Return Due Date</p>
                <p className="text-sm text-white font-bold">31st July 2025</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Overlay removed - content now shifts when sidebar opens */}
      
      {/* SIA Message Overlay */}
      {formSiaMessage && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 max-w-2xl w-full px-4">
          <div className="bg-gradient-to-r from-purple-900/90 via-blue-900/90 to-purple-900/90 border border-purple-500/30 rounded-2xl px-6 py-4 backdrop-blur-md shadow-lg">
            <div className="flex items-start gap-4">
              <SIAOrb isSpeaking={formSiaSpeaking} size="small" />
              <p className="text-gray-100 text-sm leading-relaxed flex-1">{formSiaMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content - shifts right when sidebar opens */}
      <div className={`flex-1 bg-[#0a0f1c] p-6 overflow-y-auto transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-0'}`}>
        <div className="flex items-center justify-between mb-6 pl-12">
          <div>
            <h2 className="text-xl font-semibold text-white">Form 24Q - Deductor Information</h2>
            <p className="text-gray-400 text-sm">Enter the details of the person/organization deducting TDS</p>
          </div>
          <Badge className="bg-blue-500/10 text-blue-400">Step 1 of {getTotalSteps()}</Badge>
        </div>

        <div className="space-y-6 pl-12">
          {/* Return Period */}
          <Card className="bg-[#111827] border-white/10">
            <CardHeader className="pb-4">
              <CardTitle className="text-white text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-blue-400" />Return Period</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-300 mb-2 block">Financial Year *</Label>
                <select className="w-full bg-[#0a0f1c] border border-white/10 rounded-lg p-3 text-white" value={formData.financialYear} onChange={(e) => setFormData({...formData, financialYear: e.target.value})}>
                  <option value="2025-26">2025-26</option>
                  <option value="2024-25">2024-25</option>
                  <option value="2023-24">2023-24</option>
                </select>
              </div>
              <div>
                <Label className="text-gray-300 mb-2 block">Assessment Year *</Label>
                <select className="w-full bg-[#0a0f1c] border border-white/10 rounded-lg p-3 text-white" value={formData.assessmentYear} onChange={(e) => setFormData({...formData, assessmentYear: e.target.value})}>
                  {ASSESSMENT_YEARS.map(ay => <option key={ay} value={ay}>{ay}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-300 mb-2 block">Quarter *</Label>
                <select className="w-full bg-[#0a0f1c] border border-white/10 rounded-lg p-3 text-white" value={formData.quarter} onChange={(e) => setFormData({...formData, quarter: e.target.value})}>
                  {QUARTERS.map(q => <option key={q.id} value={q.id}>{q.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-300 mb-2 block">Form Type *</Label>
                <select className="w-full bg-[#0a0f1c] border border-white/10 rounded-lg p-3 text-white" value={formData.formType} onChange={(e) => setFormData({...formData, formType: e.target.value})}>
                  <option value="Regular">Regular</option>
                  <option value="Correction">Correction</option>
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Deductor Identification */}
          <Card className="bg-[#111827] border-white/10">
            <CardHeader className="pb-4">
              <CardTitle className="text-white text-lg flex items-center gap-2"><Building className="w-5 h-5 text-blue-400" />Deductor Identification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">TAN (Tax Deduction Account Number) *</Label>
                  <Input className="bg-[#0a0f1c] border-white/10 text-white uppercase" placeholder="e.g., DELP12345A" maxLength={10} value={formData.tanNumber} onChange={(e) => setFormData({...formData, tanNumber: e.target.value.toUpperCase()})} />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">PAN of Deductor *</Label>
                  <Input className="bg-[#0a0f1c] border-white/10 text-white uppercase" placeholder="e.g., AAACP1234A" maxLength={10} value={formData.panNumber} onChange={(e) => setFormData({...formData, panNumber: e.target.value.toUpperCase()})} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Name of Deductor *</Label>
                  <Input className="bg-[#0a0f1c] border-white/10 text-white" placeholder="Company/Individual Name" value={formData.deductorName} onChange={(e) => setFormData({...formData, deductorName: e.target.value})} />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Branch/Division</Label>
                  <Input className="bg-[#0a0f1c] border-white/10 text-white" placeholder="Branch name (if applicable)" value={formData.branchDivision} onChange={(e) => setFormData({...formData, branchDivision: e.target.value})} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="bg-[#111827] border-white/10">
            <CardHeader className="pb-4">
              <CardTitle className="text-white text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-blue-400" />Address Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="text-gray-300 mb-2 block">Flat/Door/Block No.</Label><Input className="bg-[#0a0f1c] border-white/10 text-white" value={formData.flatNo} onChange={(e) => setFormData({...formData, flatNo: e.target.value})} /></div>
                <div><Label className="text-gray-300 mb-2 block">Building/Premises Name</Label><Input className="bg-[#0a0f1c] border-white/10 text-white" value={formData.buildingName} onChange={(e) => setFormData({...formData, buildingName: e.target.value})} /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="text-gray-300 mb-2 block">Road/Street/Lane</Label><Input className="bg-[#0a0f1c] border-white/10 text-white" value={formData.roadStreet} onChange={(e) => setFormData({...formData, roadStreet: e.target.value})} /></div>
                <div><Label className="text-gray-300 mb-2 block">Area/Locality</Label><Input className="bg-[#0a0f1c] border-white/10 text-white" value={formData.area} onChange={(e) => setFormData({...formData, area: e.target.value})} /></div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div><Label className="text-gray-300 mb-2 block">City *</Label><Input className="bg-[#0a0f1c] border-white/10 text-white" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} /></div>
                <div><Label className="text-gray-300 mb-2 block">State *</Label><Input className="bg-[#0a0f1c] border-white/10 text-white" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} /></div>
                <div><Label className="text-gray-300 mb-2 block">PIN Code *</Label><Input className="bg-[#0a0f1c] border-white/10 text-white" maxLength={6} value={formData.pincode} onChange={(e) => setFormData({...formData, pincode: e.target.value})} /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="text-gray-300 mb-2 block">Telephone</Label><Input className="bg-[#0a0f1c] border-white/10 text-white" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})} /></div>
                <div><Label className="text-gray-300 mb-2 block">Email *</Label><Input className="bg-[#0a0f1c] border-white/10 text-white" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /></div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={goBack} className="border-white/10 text-gray-400">Back</Button>
            <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white" onClick={() => setStage('challan')}>
              Next: Challan Details<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // ==================== CHALLAN DETAILS ====================
  const renderChallanForm = () => (
    <div className="min-h-[80vh] flex">
      <Sidebar currentStage="challan" />
      <div className="flex-1 bg-[#0a0f1c] p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={goBack} className="p-2 hover:bg-white/5 rounded-lg"><ChevronLeft className="w-5 h-5 text-gray-400" /></button>
            <div>
              <h2 className="text-xl font-semibold text-white">Challan Details (Challan 281)</h2>
              <p className="text-gray-400 text-sm">Enter details of TDS payment challans</p>
            </div>
          </div>
          <Badge className="bg-blue-500/10 text-blue-400">Step 2 of {getTotalSteps()}</Badge>
        </div>

        <Button className="mb-4 bg-gradient-to-r from-blue-500 to-cyan-500" onClick={() => setShowAddChallan(true)}>
          <Plus className="w-4 h-4 mr-2" />Add Challan
        </Button>

        <Card className="bg-[#111827] border-white/10 mb-6">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="text-left p-3 text-gray-400 font-medium text-sm">S.No</th>
                  <th className="text-left p-3 text-gray-400 font-medium text-sm">BSR Code</th>
                  <th className="text-left p-3 text-gray-400 font-medium text-sm">Challan Serial</th>
                  <th className="text-left p-3 text-gray-400 font-medium text-sm">Deposit Date</th>
                  <th className="text-right p-3 text-gray-400 font-medium text-sm">TDS (₹)</th>
                  <th className="text-right p-3 text-gray-400 font-medium text-sm">Surcharge</th>
                  <th className="text-right p-3 text-gray-400 font-medium text-sm">Edu. Cess</th>
                  <th className="text-right p-3 text-gray-400 font-medium text-sm">Interest</th>
                  <th className="text-right p-3 text-gray-400 font-medium text-sm">Total (₹)</th>
                  <th className="text-center p-3 text-gray-400 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {challans.map((c, idx) => (
                  <tr key={c.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white">{idx + 1}</td>
                    <td className="p-3 text-gray-300 font-mono">{c.bsrCode}</td>
                    <td className="p-3 text-gray-300 font-mono">{c.challanSerial}</td>
                    <td className="p-3 text-gray-300">{c.depositDate}</td>
                    <td className="p-3 text-white text-right">{c.tdsAmount.toLocaleString()}</td>
                    <td className="p-3 text-gray-300 text-right">{c.surcharge.toLocaleString()}</td>
                    <td className="p-3 text-gray-300 text-right">{c.educationCess.toLocaleString()}</td>
                    <td className="p-3 text-gray-300 text-right">{c.interest.toLocaleString()}</td>
                    <td className="p-3 text-green-400 text-right font-medium">{c.depositAmount.toLocaleString()}</td>
                    <td className="p-3 text-center">
                      <button onClick={() => handleDeleteChallan(c.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-white/5">
                  <td colSpan="4" className="p-3 text-white font-medium">Total</td>
                  <td className="p-3 text-white text-right font-medium">{challans.reduce((s,c) => s + c.tdsAmount, 0).toLocaleString()}</td>
                  <td className="p-3 text-white text-right font-medium">{challans.reduce((s,c) => s + c.surcharge, 0).toLocaleString()}</td>
                  <td className="p-3 text-white text-right font-medium">{challans.reduce((s,c) => s + c.educationCess, 0).toLocaleString()}</td>
                  <td className="p-3 text-white text-right font-medium">{challans.reduce((s,c) => s + c.interest, 0).toLocaleString()}</td>
                  <td className="p-3 text-green-400 text-right font-bold">{totalChallanAmount.toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>

        {/* Add Challan Modal */}
        {showAddChallan && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl bg-[#111827] border-white/10 max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="text-white">Add Challan (Form 281)</CardTitle>
                <CardDescription className="text-gray-400">Enter TDS payment challan details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-gray-300">BSR Code (7 digits) *</Label><Input className="bg-[#0a0f1c] border-white/10 text-white mt-1" maxLength={7} value={newChallan.bsrCode} onChange={(e) => setNewChallan({...newChallan, bsrCode: e.target.value})} /></div>
                  <div><Label className="text-gray-300">Challan Serial No. *</Label><Input className="bg-[#0a0f1c] border-white/10 text-white mt-1" maxLength={5} value={newChallan.challanSerial} onChange={(e) => setNewChallan({...newChallan, challanSerial: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-gray-300">Deposit Date *</Label><Input type="date" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newChallan.depositDate} onChange={(e) => setNewChallan({...newChallan, depositDate: e.target.value})} /></div>
                  <div><Label className="text-gray-300">Bank Name</Label><Input className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newChallan.bankName} onChange={(e) => setNewChallan({...newChallan, bankName: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label className="text-gray-300">TDS Amount *</Label><Input type="number" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newChallan.tdsAmount} onChange={(e) => setNewChallan({...newChallan, tdsAmount: e.target.value})} /></div>
                  <div><Label className="text-gray-300">Surcharge</Label><Input type="number" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newChallan.surcharge} onChange={(e) => setNewChallan({...newChallan, surcharge: e.target.value})} /></div>
                  <div><Label className="text-gray-300">Education Cess *</Label><Input type="number" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newChallan.educationCess} onChange={(e) => setNewChallan({...newChallan, educationCess: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label className="text-gray-300">Interest (if any)</Label><Input type="number" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newChallan.interest} onChange={(e) => setNewChallan({...newChallan, interest: e.target.value})} /></div>
                  <div><Label className="text-gray-300">Fee u/s 234E</Label><Input type="number" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newChallan.fee234E} onChange={(e) => setNewChallan({...newChallan, fee234E: e.target.value})} /></div>
                  <div><Label className="text-gray-300">Total Amount *</Label><Input type="number" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newChallan.depositAmount} onChange={(e) => setNewChallan({...newChallan, depositAmount: e.target.value})} /></div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" className="flex-1 border-white/10" onClick={() => setShowAddChallan(false)}>Cancel</Button>
                  <Button className="flex-1 bg-blue-500" onClick={handleAddChallan}>Add Challan</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={goBack} className="border-white/10 text-gray-400">Back</Button>
          <Button className="bg-gradient-to-r from-blue-500 to-cyan-500" onClick={() => setStage('annexure1')}>
            Next: Annexure I - Employees<ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );

  // ==================== ANNEXURE I - EMPLOYEE DETAILS ====================
  const renderAnnexure1 = () => (
    <div className="min-h-[80vh] flex">
      <Sidebar currentStage="annexure1" />
      <div className="flex-1 bg-[#0a0f1c] p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={goBack} className="p-2 hover:bg-white/5 rounded-lg"><ChevronLeft className="w-5 h-5 text-gray-400" /></button>
            <div>
              <h2 className="text-xl font-semibold text-white">Annexure I - Deductee (Employee) Details</h2>
              <p className="text-gray-400 text-sm">Employee-wise TDS deduction details for the quarter</p>
            </div>
          </div>
          <Badge className="bg-blue-500/10 text-blue-400">Step 3 of {getTotalSteps()}</Badge>
        </div>

        <div className="flex gap-4 mb-4">
          <Button className="bg-gradient-to-r from-blue-500 to-cyan-500" onClick={() => setShowAddDeductee(true)}>
            <Plus className="w-4 h-4 mr-2" />Add Employee
          </Button>
          <div className="ml-auto flex items-center gap-2 text-sm">
            <span className="text-gray-400">Challan Match:</span>
            <Badge className={totalTaxDeducted <= totalChallanAmount ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}>
              {totalTaxDeducted <= totalChallanAmount ? '✓ Matched' : `Shortfall: ₹${(totalTaxDeducted - totalChallanAmount).toLocaleString()}`}
            </Badge>
          </div>
        </div>

        <Card className="bg-[#111827] border-white/10 mb-6">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="text-left p-3 text-gray-400 font-medium text-xs">S.No</th>
                  <th className="text-left p-3 text-gray-400 font-medium text-xs">Emp Ref</th>
                  <th className="text-left p-3 text-gray-400 font-medium text-xs">PAN</th>
                  <th className="text-left p-3 text-gray-400 font-medium text-xs">Employee Name</th>
                  <th className="text-left p-3 text-gray-400 font-medium text-xs">Payment Date</th>
                  <th className="text-center p-3 text-gray-400 font-medium text-xs">Section</th>
                  <th className="text-right p-3 text-gray-400 font-medium text-xs">Gross Salary (₹)</th>
                  <th className="text-right p-3 text-gray-400 font-medium text-xs">TDS (₹)</th>
                  <th className="text-right p-3 text-gray-400 font-medium text-xs">Cess (₹)</th>
                  <th className="text-right p-3 text-gray-400 font-medium text-xs">Total Tax (₹)</th>
                  <th className="text-center p-3 text-gray-400 font-medium text-xs">Challan</th>
                  <th className="text-center p-3 text-gray-400 font-medium text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deductees.map((d) => (
                  <tr key={d.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white">{d.slNo}</td>
                    <td className="p-3 text-gray-300 text-sm">{d.employeeRefNo}</td>
                    <td className="p-3 text-gray-300 font-mono text-sm">{d.pan}</td>
                    <td className="p-3 text-white">{d.name}</td>
                    <td className="p-3 text-gray-300 text-sm">{d.dateOfPayment}</td>
                    <td className="p-3 text-center"><Badge className="bg-blue-500/10 text-blue-400 text-xs">{d.section}</Badge></td>
                    <td className="p-3 text-white text-right">{d.grossSalary.toLocaleString()}</td>
                    <td className="p-3 text-white text-right">{d.tdsDeducted.toLocaleString()}</td>
                    <td className="p-3 text-gray-300 text-right">{d.educationCess.toLocaleString()}</td>
                    <td className="p-3 text-green-400 text-right font-medium">{d.totalTax.toLocaleString()}</td>
                    <td className="p-3 text-center text-gray-300">#{d.challanId}</td>
                    <td className="p-3 text-center">
                      <button onClick={() => handleDeleteDeductee(d.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-white/5">
                  <td colSpan="6" className="p-3 text-white font-medium">Total ({deductees.length} employees)</td>
                  <td className="p-3 text-white text-right font-medium">{totalGrossSalary.toLocaleString()}</td>
                  <td className="p-3 text-white text-right font-medium">{totalTDSDeducted.toLocaleString()}</td>
                  <td className="p-3 text-white text-right font-medium">{totalCessDeducted.toLocaleString()}</td>
                  <td className="p-3 text-green-400 text-right font-bold">{totalTaxDeducted.toLocaleString()}</td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>

        {/* Add Deductee Modal */}
        {showAddDeductee && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl bg-[#111827] border-white/10 max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="text-white">Add Employee (Deductee)</CardTitle>
                <CardDescription className="text-gray-400">Enter employee TDS deduction details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-gray-300">Employee Reference No.</Label><Input className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newDeductee.employeeRefNo} onChange={(e) => setNewDeductee({...newDeductee, employeeRefNo: e.target.value})} /></div>
                  <div><Label className="text-gray-300">PAN *</Label><Input className="bg-[#0a0f1c] border-white/10 text-white mt-1 uppercase" maxLength={10} value={newDeductee.pan} onChange={(e) => setNewDeductee({...newDeductee, pan: e.target.value.toUpperCase()})} /></div>
                </div>
                <div><Label className="text-gray-300">Employee Name *</Label><Input className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newDeductee.name} onChange={(e) => setNewDeductee({...newDeductee, name: e.target.value})} /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label className="text-gray-300">Payment Date *</Label><Input type="date" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newDeductee.dateOfPayment} onChange={(e) => setNewDeductee({...newDeductee, dateOfPayment: e.target.value})} /></div>
                  <div>
                    <Label className="text-gray-300">Section *</Label>
                    <select className="w-full bg-[#0a0f1c] border border-white/10 rounded-lg p-3 text-white mt-1" value={newDeductee.section} onChange={(e) => setNewDeductee({...newDeductee, section: e.target.value})}>
                      {TDS_SECTIONS.map(s => <option key={s.code} value={s.code}>{s.code} - {s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Link to Challan *</Label>
                    <select className="w-full bg-[#0a0f1c] border border-white/10 rounded-lg p-3 text-white mt-1" value={newDeductee.challanId} onChange={(e) => setNewDeductee({...newDeductee, challanId: e.target.value})}>
                      <option value="">Select Challan</option>
                      {challans.map(c => <option key={c.id} value={c.id}>#{c.id} - {c.challanSerial} (₹{c.depositAmount.toLocaleString()})</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label className="text-gray-300">Gross Salary (₹) *</Label><Input type="number" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newDeductee.grossSalary} onChange={(e) => setNewDeductee({...newDeductee, grossSalary: e.target.value})} /></div>
                  <div><Label className="text-gray-300">TDS Deducted (₹) *</Label><Input type="number" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newDeductee.tdsDeducted} onChange={(e) => setNewDeductee({...newDeductee, tdsDeducted: e.target.value})} /></div>
                  <div><Label className="text-gray-300">Education Cess (₹)</Label><Input type="number" className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newDeductee.educationCess} onChange={(e) => setNewDeductee({...newDeductee, educationCess: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Reason for Deduction</Label>
                    <select className="w-full bg-[#0a0f1c] border border-white/10 rounded-lg p-3 text-white mt-1" value={newDeductee.deductionReason} onChange={(e) => setNewDeductee({...newDeductee, deductionReason: e.target.value})}>
                      <option value="">Not Applicable</option>
                      {DEDUCTION_REASONS.map(r => <option key={r.code} value={r.code}>{r.code} - {r.desc}</option>)}
                    </select>
                  </div>
                  <div><Label className="text-gray-300">Certificate No. (if u/s 197)</Label><Input className="bg-[#0a0f1c] border-white/10 text-white mt-1" value={newDeductee.certificateNo} onChange={(e) => setNewDeductee({...newDeductee, certificateNo: e.target.value})} /></div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" className="flex-1 border-white/10" onClick={() => setShowAddDeductee(false)}>Cancel</Button>
                  <Button className="flex-1 bg-blue-500" onClick={handleAddDeductee}>Add Employee</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={goBack} className="border-white/10 text-gray-400">Back</Button>
          <Button className="bg-gradient-to-r from-blue-500 to-cyan-500" onClick={() => setStage(formData.quarter === 'Q4' ? 'annexure2' : 'summary')}>
            {formData.quarter === 'Q4' ? 'Next: Annexure II' : 'Next: Review & Submit'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );

  // ==================== ANNEXURE II (Q4 Only) ====================
  const renderAnnexure2 = () => (
    <div className="min-h-[80vh] flex">
      <Sidebar currentStage="annexure2" />
      <div className="flex-1 bg-[#0a0f1c] p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={goBack} className="p-2 hover:bg-white/5 rounded-lg"><ChevronLeft className="w-5 h-5 text-gray-400" /></button>
            <div>
              <h2 className="text-xl font-semibold text-white">Annexure II - Salary Breakup (Q4 Only)</h2>
              <p className="text-gray-400 text-sm">Annual salary details and tax computation for employees</p>
            </div>
          </div>
          <Badge className="bg-amber-500/10 text-amber-400">Q4 Only</Badge>
        </div>

        <Card className="bg-[#111827] border-white/10 mb-6 p-6">
          <div className="flex items-center gap-4 mb-4">
            <AlertCircle className="w-8 h-8 text-amber-400" />
            <div>
              <h3 className="text-white font-medium">Annexure II - Annual Salary Details</h3>
              <p className="text-gray-400 text-sm">This annexure contains employee-wise annual salary breakup including:</p>
            </div>
          </div>
          <ul className="list-disc list-inside text-gray-300 space-y-2 ml-12">
            <li>Gross salary including all allowances</li>
            <li>Exemptions under Section 10</li>
            <li>Deductions under Chapter VI-A (80C, 80D, etc.)</li>
            <li>Income from other sources as reported</li>
            <li>Tax computation and Form 16 data</li>
          </ul>
          <p className="text-gray-500 text-sm mt-4 ml-12">Note: This section is typically auto-populated from payroll data in actual TRACES portal.</p>
        </Card>

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={goBack} className="border-white/10 text-gray-400">Back</Button>
          <Button className="bg-gradient-to-r from-blue-500 to-cyan-500" onClick={() => setStage('summary')}>
            Next: Review & Submit<ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );

  // ==================== SUMMARY & SUBMIT ====================
  const renderSummary = () => (
    <div className="min-h-[80vh] flex">
      <Sidebar currentStage="summary" />
      <div className="flex-1 bg-[#0a0f1c] p-6 overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={goBack} className="p-2 hover:bg-white/5 rounded-lg"><ChevronLeft className="w-5 h-5 text-gray-400" /></button>
            <div>
              <h2 className="text-xl font-semibold text-white">Summary & Submit</h2>
              <p className="text-gray-400 text-sm">Review your Form 24Q before submission</p>
            </div>
          </div>
          <Badge className="bg-green-500/10 text-green-400">Final Step</Badge>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card className="bg-[#111827] border-white/10">
            <CardHeader><CardTitle className="text-white text-lg">Deductor Details</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">TAN:</span><span className="text-white font-mono">{formData.tanNumber || 'DELP12345A'}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Name:</span><span className="text-white">{formData.deductorName || 'Demo Company Pvt Ltd'}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">FY / AY:</span><span className="text-white">{formData.financialYear} / {formData.assessmentYear}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Quarter:</span><span className="text-white">{formData.quarter}</span></div>
            </CardContent>
          </Card>

          <Card className="bg-[#111827] border-white/10">
            <CardHeader><CardTitle className="text-white text-lg">TDS Summary</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">Total Employees:</span><span className="text-white">{deductees.length}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Total Gross Salary:</span><span className="text-white">₹{totalGrossSalary.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Total TDS Deducted:</span><span className="text-green-400 font-bold">₹{totalTaxDeducted.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Total Challan Deposited:</span><span className="text-blue-400">₹{totalChallanAmount.toLocaleString()}</span></div>
            </CardContent>
          </Card>
        </div>

        {/* Verification Status */}
        <Card className={`mb-6 ${totalTaxDeducted <= totalChallanAmount ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
          <CardContent className="p-4 flex items-center gap-4">
            {totalTaxDeducted <= totalChallanAmount ? (
              <>
                <CheckCircle2 className="w-8 h-8 text-green-400" />
                <div>
                  <h4 className="text-green-400 font-medium">Challan Verification Passed</h4>
                  <p className="text-gray-400 text-sm">TDS deducted matches with challan deposits</p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-8 h-8 text-red-400" />
                <div>
                  <h4 className="text-red-400 font-medium">Challan Shortfall Detected</h4>
                  <p className="text-gray-400 text-sm">Please add more challans or verify deductee amounts</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={goBack} className="border-white/10 text-gray-400">Back</Button>
          <Button className="bg-gradient-to-r from-green-500 to-emerald-500" onClick={() => {
            const tokenNum = 'TDS24Q' + formData.financialYear.replace('-','') + formData.quarter + Math.random().toString().slice(2,8);
            setTdsTokenNumber(tokenNum);
            setShowTDSSuccessModal(true);
          }}>
            <CheckCircle2 className="w-4 h-4 mr-2" />Submit Form 24Q
          </Button>
        </div>
      </div>
    </div>
  );

  // ==================== TDS RATE CHART MODAL ====================
  const renderTDSRateChartModal = () => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowTDSRateChart(false)}>
      <div 
        className="bg-[#0d1220] border border-white/10 rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 bg-gradient-to-r from-blue-900/30 to-purple-900/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <FileText className="w-7 h-7 text-blue-400" />
                TDS Rate Chart - FY 2025-26
              </h2>
              <p className="text-sm text-gray-400 mt-1">Assessment Year 2026-27 | As amended by Finance Act, 2025</p>
            </div>
            <button 
              onClick={() => setShowTDSRateChart(false)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-rate-chart-modal"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex gap-2 mt-4">
            {[
              { id: 'all', label: 'All Sections' },
              { id: 'resident', label: 'Residents' },
              { id: 'non-resident', label: 'Non-Residents (Sec 195)' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setRateChartFilter(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  rateChartFilter === tab.id 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`filter-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Table */}
        <div className="overflow-auto max-h-[calc(85vh-180px)]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#151c2c] z-10">
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-gray-400 font-medium w-24">Section</th>
                <th className="text-left p-4 text-gray-400 font-medium">Nature of Payment</th>
                <th className="text-center p-4 text-gray-400 font-medium w-32">TDS Rate</th>
                <th className="text-left p-4 text-gray-400 font-medium w-56">Threshold Limit</th>
              </tr>
            </thead>
            <tbody>
              {TDS_RATE_CHART_DATA
                .filter(item => rateChartFilter === 'all' || item.category === rateChartFilter)
                .map((item, idx) => (
                  <tr 
                    key={idx} 
                    className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
                      item.category === 'non-resident' ? 'bg-amber-500/5' : ''
                    }`}
                  >
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-mono ${
                        item.category === 'non-resident' 
                          ? 'bg-amber-500/20 text-amber-300' 
                          : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {item.section}
                      </span>
                    </td>
                    <td className="p-4 text-gray-200">{item.nature}</td>
                    <td className="p-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        parseFloat(item.rate) >= 20 
                          ? 'bg-red-500/20 text-red-300' 
                          : parseFloat(item.rate) >= 10 
                            ? 'bg-yellow-500/20 text-yellow-300' 
                            : 'bg-green-500/20 text-green-300'
                      }`}>
                        {item.rate}
                      </span>
                    </td>
                    <td className="p-4 text-gray-400 text-xs">{item.threshold}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-[#0a0f1a]">
          <p className="text-xs text-gray-500 text-center">
            Note: Rates are subject to applicable surcharge and cess. Higher rates apply if PAN not furnished (Sec 206AA). 
            Verify rates from official Income Tax Department notifications.
          </p>
        </div>
      </div>
    </div>
  );

  // ==================== SIA - SETU INTELLIGENT ASSISTANT ====================
  const SIAAssistant = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isSiaResponding, setIsSiaResponding] = useState(false); // For seamless voice feel
    const messagesEndRef = React.useRef(null);
    const mediaRecorderRef = React.useRef(null);
    const audioChunksRef = React.useRef([]);
    const siaVoiceAudioRef = React.useRef(null);

    // Initial greeting when chat opens
    React.useEffect(() => {
      if (isOpen && messages.length === 0) {
        setMessages([{
          id: 1,
          sender: 'sia',
          text: "Hi! I am SIA, Setu Intelligent Assistant. How can I help you with your TDS filing today?",
          timestamp: new Date()
        }]);
      }
    }, [isOpen]);

    // Auto scroll to bottom
    React.useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Cleanup audio on unmount
    React.useEffect(() => {
      return () => {
        if (siaVoiceAudioRef.current) {
          siaVoiceAudioRef.current.pause();
          siaVoiceAudioRef.current = null;
        }
      };
    }, []);

    // Start voice recording (push-to-talk)
    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = async () => {
          // Stop all tracks
          stream.getTracks().forEach(track => track.stop());
          
          // Process the recorded audio
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await processVoiceInput(audioBlob);
        };

        mediaRecorder.start();
        setIsRecording(true);
      } catch (err) {
        console.error('Microphone access denied:', err);
        // Show error message
        setMessages(prev => [...prev, {
          id: Date.now(),
          sender: 'sia',
          text: "I couldn't access your microphone. Please allow microphone access and try again.",
          timestamp: new Date()
        }]);
      }
    };

    // Stop voice recording
    const stopRecording = () => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        setIsSiaResponding(true); // Show SIA is about to respond
      }
    };

    // Process voice input - send to backend for transcription + LLM + TTS
    const processVoiceInput = async (audioBlob) => {
      try {
        // Convert blob to base64
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1]; // Remove data:audio/webm;base64, prefix
          
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          
          // Build history for context
          const history = messages.slice(-6).map(m => ({
            role: m.sender === 'user' ? 'user' : 'assistant',
            content: m.text
          }));

          const response = await fetch(`${API_URL}/api/tds/sia-voice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              audio_base64: base64Audio,
              history: history
            })
          });

          const data = await response.json();
          
          if (data.success && data.user_text) {
            // Add user's transcribed message
            setMessages(prev => [...prev, {
              id: Date.now(),
              sender: 'user',
              text: data.user_text,
              timestamp: new Date(),
              isVoice: true
            }]);

            // Add SIA's response
            setTimeout(() => {
              setMessages(prev => [...prev, {
                id: Date.now() + 1,
                sender: 'sia',
                text: data.sia_text,
                timestamp: new Date(),
                isVoice: true
              }]);

              // Play SIA's voice response
              if (data.sia_audio) {
                playSiaVoice(data.sia_audio);
              } else {
                setIsSiaResponding(false);
              }
            }, 100);
          } else if (data.sia_text) {
            // Handle cases where transcription failed but we have a message
            setMessages(prev => [...prev, {
              id: Date.now(),
              sender: 'sia',
              text: data.sia_text,
              timestamp: new Date()
            }]);
            setIsSiaResponding(false);
          } else {
            setIsSiaResponding(false);
          }
        };
      } catch (err) {
        console.error('Voice processing error:', err);
        setMessages(prev => [...prev, {
          id: Date.now(),
          sender: 'sia',
          text: "I couldn't process that. Please try again.",
          timestamp: new Date()
        }]);
        setIsSiaResponding(false);
      }
    };

    // Play SIA's voice response
    const playSiaVoice = (audioBase64) => {
      try {
        // Stop any existing audio
        if (siaVoiceAudioRef.current) {
          siaVoiceAudioRef.current.pause();
        }

        const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
        siaVoiceAudioRef.current = audio;
        
        audio.onended = () => {
          setIsSiaResponding(false);
        };
        
        audio.onerror = () => {
          setIsSiaResponding(false);
        };

        audio.play();
      } catch (err) {
        console.error('Audio playback error:', err);
        setIsSiaResponding(false);
      }
    };

    // Context-aware suggestions
    const getSuggestions = () => {
      switch(stage) {
        case 'start': return ['How to start filing?', 'What is Form 24Q?', 'TDS due dates'];
        case 'form': return ['What is TAN?', 'Deductor details help', 'Address format'];
        case 'challan': return ['What is BSR code?', 'Challan 281 help', 'How to add challan?'];
        case 'annexure1': return ['Add employee help', 'Section 192 explained', 'TDS calculation'];
        case 'summary': return ['Verify details', 'Submit process', 'Download receipt'];
        default: return ['What is TDS?', 'Filing process', 'Help with forms'];
      }
    };

    // Simulated AI response
    const getAIResponse = (userMessage) => {
      const lowerMsg = userMessage.toLowerCase();
      if (lowerMsg.includes('tan') || lowerMsg.includes('tax deduction')) {
        return "TAN (Tax Deduction Account Number) is a 10-character alphanumeric code required for deducting TDS. Format: DELP12345A - first 4 letters are location code, next 5 are digits, last is a check letter.";
      } else if (lowerMsg.includes('bsr') || lowerMsg.includes('challan')) {
        return "BSR Code is a 7-digit code assigned to the bank branch where you deposited TDS. You can find it on your challan receipt (Challan 281). The Challan Serial Number is also on the receipt.";
      } else if (lowerMsg.includes('due date') || lowerMsg.includes('deadline')) {
        return "TDS Return Due Dates for FY 2025-26:\n• Q1 (Apr-Jun): 31st July\n• Q2 (Jul-Sep): 31st October\n• Q3 (Oct-Dec): 31st January\n• Q4 (Jan-Mar): 31st May";
      } else if (lowerMsg.includes('section 192') || lowerMsg.includes('salary')) {
        return "Section 192 covers TDS on salary payments. As an employer, you must deduct tax at applicable slab rates from employee salaries and deposit it with the government using Challan 281.";
      } else if (lowerMsg.includes('annexure') || lowerMsg.includes('employee')) {
        return "Annexure I contains employee-wise details: PAN, Name, Gross Salary, TDS deducted. Each employee record must be linked to a challan. Annexure II (Q4 only) has detailed salary breakup.";
      } else if (lowerMsg.includes('form 24q') || lowerMsg.includes('what is')) {
        return "Form 24Q is a quarterly TDS return filed by employers for TDS deducted on salaries (Section 192). It contains deductor details, challan information, and employee-wise deduction records.";
      } else if (lowerMsg.includes('help') || lowerMsg.includes('start')) {
        return "To file Form 24Q:\n1. Enter Deductor Details (TAN, PAN, Address)\n2. Add Challan 281 payment details\n3. Fill Annexure I with employee TDS records\n4. Review and Submit\n\nClick any step in the sidebar to navigate!";
      } else {
        return "I can help you with TDS filing queries. Try asking about:\n• TAN/PAN requirements\n• Challan details (BSR code)\n• Employee records\n• Due dates\n• Form 24Q sections";
      }
    };

    const handleSend = async () => {
      if (!inputValue.trim()) return;
      
      const userMsg = {
        id: messages.length + 1,
        sender: 'user',
        text: inputValue.trim(),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMsg]);
      const userInput = inputValue.trim();
      setInputValue('');
      setIsTyping(true);

      try {
        // Call SIA Chat API
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/tds/sia-chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: userInput,
            history: messages.slice(-6).map(m => ({
              role: m.sender === 'user' ? 'user' : 'assistant',
              content: m.text
            }))
          })
        });
        
        const data = await response.json();
        
        const aiResponse = {
          id: messages.length + 2,
          sender: 'sia',
          text: data.success ? data.response : "I'm having trouble right now. Please try again.",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
      } catch (err) {
        console.log('SIA chat error:', err);
        const aiResponse = {
          id: messages.length + 2,
          sender: 'sia',
          text: "I couldn't process that. Please try again.",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
      }
      
      setIsTyping(false);
    };

    const handleSuggestionClick = (suggestion) => {
      setInputValue(suggestion);
      handleSend();
    };

    return (
      <div className="fixed bottom-6 right-6 z-50">
        {/* Chat Window */}
        <div className={`absolute bottom-20 right-0 w-[380px] transition-all duration-500 ease-out ${
          isOpen 
            ? 'opacity-100 translate-y-0 pointer-events-auto' 
            : 'opacity-0 translate-y-8 pointer-events-none'
        }`}>
          <div className="bg-gradient-to-b from-[#0d1220] to-[#0a0f1c] border border-purple-500/20 rounded-2xl shadow-2xl shadow-purple-500/10 overflow-hidden">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-cyan-600/20 p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-400 p-[2px]">
                    <div className="w-full h-full rounded-full bg-[#0d1220] flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 via-blue-400 to-cyan-300 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#0d1220]"></div>
                </div>
                <div>
                  <h3 className="text-white font-semibold">SIA</h3>
                  <p className="text-xs text-gray-400">Setu Intelligent Assistant</p>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="ml-auto p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="h-[320px] overflow-y-auto p-4 space-y-4 custom-scrollbar">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex gap-3 ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                  {msg.sender === 'sia' && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-400 p-[2px] flex-shrink-0">
                      <div className="w-full h-full rounded-full bg-[#0d1220] flex items-center justify-center">
                        <div className="w-4 h-4 rounded-full bg-gradient-to-br from-purple-400 to-cyan-300"></div>
                      </div>
                    </div>
                  )}
                  <div className={`max-w-[80%] ${
                    msg.sender === 'user' 
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' 
                      : 'bg-white/5 text-gray-200 border border-white/10'
                  } rounded-2xl px-4 py-3 text-sm whitespace-pre-line`}>
                    {msg.isVoice && (
                      <Mic className="w-3 h-3 inline mr-1 opacity-60" />
                    )}
                    {msg.text}
                  </div>
                </div>
              ))}
              
              {/* Typing indicator */}
              {isTyping && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-400 p-[2px] flex-shrink-0">
                    <div className="w-full h-full rounded-full bg-[#0d1220] flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-br from-purple-400 to-cyan-300"></div>
                    </div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            <div className="px-4 pb-2">
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {getSuggestions().map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInputValue(suggestion);
                      setTimeout(() => handleSend(), 100);
                    }}
                    className="flex-shrink-0 px-3 py-1.5 text-xs bg-white/5 hover:bg-purple-500/20 border border-white/10 hover:border-purple-500/30 rounded-full text-gray-300 hover:text-white transition-all"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-white/10">
              {/* Voice Recording Active Indicator */}
              {isRecording && (
                <div className="mb-3 flex items-center justify-center gap-2 py-2 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-red-400 text-sm font-medium">Listening... Release to send</span>
                </div>
              )}
              
              {/* SIA Responding Indicator - Seamless feel */}
              {isSiaResponding && !isRecording && (
                <div className="mb-3 flex items-center justify-center gap-2 py-2 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                  <Volume2 className="w-4 h-4 text-purple-400 animate-pulse" />
                  <span className="text-purple-400 text-sm font-medium">SIA is speaking...</span>
                </div>
              )}
              
              <div className="flex gap-2">
                {/* Voice Input Button - Push to Talk */}
                <button
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                  onMouseLeave={() => isRecording && stopRecording()}
                  onTouchStart={startRecording}
                  onTouchEnd={stopRecording}
                  disabled={isSiaResponding}
                  className={`px-4 py-3 rounded-xl transition-all ${
                    isRecording 
                      ? 'bg-red-500 scale-110 shadow-lg shadow-red-500/50' 
                      : isSiaResponding
                        ? 'bg-purple-500/50 cursor-not-allowed'
                        : 'bg-white/10 hover:bg-white/20 border border-white/10 hover:border-cyan-500/50'
                  }`}
                  data-testid="voice-input-btn"
                  title="Hold to speak"
                >
                  {isRecording ? (
                    <Mic className="w-5 h-5 text-white animate-pulse" />
                  ) : (
                    <Mic className="w-5 h-5 text-cyan-400" />
                  )}
                </button>
                
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  placeholder={isRecording ? "Listening..." : "Ask SIA anything..."}
                  disabled={isRecording || isSiaResponding}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-purple-500/50 disabled:opacity-50"
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isRecording || isSiaResponding}
                  className="px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all"
                >
                  <ArrowRight className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* Voice hint */}
              <p className="text-center text-xs text-gray-500 mt-2">
                Hold <Mic className="w-3 h-3 inline text-cyan-400" /> to speak with SIA
              </p>
            </div>
          </div>
        </div>

        {/* SIA Orb Button */}
        <SIAOrb 
          isSpeaking={isSiaResponding} 
          size="medium" 
          onClick={() => setIsOpen(!isOpen)}
          className={siaActivated ? 'scale-125' : ''}
        />

        {/* Notification dot when closed */}
        {!isOpen && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full border-2 border-[#0a0f1c] animate-pulse"></div>
        )}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================
  
  // Timeline renders WITHOUT Layout for full-screen immersive experience
  if (stage === 'timeline') {
    return <TDSEducationalTimeline 
      key="tds-timeline" 
      onComplete={() => {
        setSkippedTimeline(false); // Completed timeline
        setStage('form');
      }} 
      onSkip={() => {
        setSkippedTimeline(true); // Skipped timeline
        setStage('form');
      }}
    />;
  }
  
  // Form filling stages AND start screen use SimplifiedHeader (cleaner navigation)
  const isFormFillingStage = ['start', 'form', 'challan', 'annexure1', 'annexure2', 'summary'].includes(stage);
  
  // Handle back navigation for form stages
  const handleFormBack = () => {
    const stageOrder = ['select', 'start', 'timeline', 'form', 'challan', 'annexure1', 'annexure2', 'summary'];
    const currentIndex = stageOrder.indexOf(stage);
    if (currentIndex > 0) {
      // Go to previous stage, but skip timeline (go to select instead)
      if (stageOrder[currentIndex - 1] === 'timeline') {
        setStage('select');
      } else if (stage === 'start') {
        setStage('select');
      } else {
        setStage(stageOrder[currentIndex - 1]);
      }
    }
  };
  
  if (isFormFillingStage) {
    return (
      <div className="min-h-screen bg-[#0A0F1C]">
        <SimplifiedHeader onBack={handleFormBack} showBackButton={true} />
        
        <div className="relative">
          {stage === 'start' && renderStartScreen()}
          {stage === 'form' && renderDeductorForm()}
          {stage === 'challan' && renderChallanForm()}
          {stage === 'annexure1' && renderAnnexure1()}
          {stage === 'annexure2' && renderAnnexure2()}
          {stage === 'summary' && renderSummary()}
          
          {/* SIA Assistant - only on form stages, not start */}
          {stage !== 'start' && <SIAAssistant />}
        </div>
        
        {/* TDS Rate Chart Modal */}
        {showTDSRateChart && renderTDSRateChartModal()}
        
        {/* TDS Form Submission Success Modal */}
        <Dialog open={showTDSSuccessModal} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md bg-[#0d1220] border-green-500/30">
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-400" />
              </div>
              <DialogTitle className="text-2xl text-green-400 mb-2">
                Return Submitted!
              </DialogTitle>
              <DialogDescription className="text-gray-400 mb-6">
                Your Form 24Q TDS Return has been successfully submitted.
              </DialogDescription>
              <div className="bg-[#111827] rounded-lg p-4 mb-6 border border-white/10">
                <p className="text-sm text-gray-500 mb-1">Token Number</p>
                <p className="text-2xl font-mono font-bold text-white">{tdsTokenNumber}</p>
              </div>
              <Button onClick={() => {
                setShowTDSSuccessModal(false);
                setShowFeedbackModal(true);
              }} className="w-full bg-gradient-to-r from-green-500 to-emerald-500">
                Continue
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Feedback Modal - shows after TDS Return submission */}
        <BetaFoundersModal 
          isOpen={showFeedbackModal} 
          onClose={() => {
            setShowFeedbackModal(false);
            setStage('select'); // Go back to form selection
          }} 
        />
      </div>
    );
  }
  
  // Only Select stage uses full Layout
  return (
    <Layout>
      <div className="min-h-screen bg-[#0A0F1C] relative">
        <div className="w-full px-6 lg:px-16 xl:px-24 py-8">
          {stage === 'select' && renderFormSelection()}
        </div>
        
        {/* TDS Registration Modal */}
        <TDSRegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => {
            setShowRegistrationModal(false);
            setPendingFormSelection(null);
          }}
          onSuccess={handleRegistrationSuccess}
          formType={pendingFormSelection?.id || '24Q'}
        />

        {/* TDS Rate Chart Modal */}
        {showTDSRateChart && renderTDSRateChartModal()}
        
        {/* TDS Form Submission Success Modal */}
        <Dialog open={showTDSSuccessModal} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md bg-[#0d1220] border-green-500/30">
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-400" />
              </div>
              <DialogTitle className="text-2xl text-green-400 mb-2">
                Return Submitted!
              </DialogTitle>
              <DialogDescription className="text-gray-400 mb-6">
                Your Form 24Q TDS Return has been successfully submitted.
              </DialogDescription>
              <div className="bg-[#111827] rounded-lg p-4 mb-6 border border-white/10">
                <p className="text-sm text-gray-500 mb-1">Token Number</p>
                <p className="text-2xl font-mono font-bold text-white">{tdsTokenNumber}</p>
              </div>
              <Button onClick={() => {
                setShowTDSSuccessModal(false);
                setShowFeedbackModal(true);
              }} className="w-full bg-gradient-to-r from-green-500 to-emerald-500">
                Continue
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Feedback Modal - shows after TDS Return submission */}
        <BetaFoundersModal 
          isOpen={showFeedbackModal} 
          onClose={() => {
            setShowFeedbackModal(false);
            setStage('select'); // Go back to form selection
          }} 
        />
      </div>
    </Layout>
  );
}
